export type DsjeFieldType =
	| "text"
	| "number"
	| "select"
	| "yesno"
	| "textarea"
	| "date"
	| "time"
	// Real-data-backed dropdowns (District master list; Institute list
	// scoped to whichever field named by `dependsOn` holds the district id;
	// Financial Year from the audit_years master table) — everything else
	// is a plain static-options field.
	| "district"
	| "institute"
	| "financial_year"
	// Block list scoped by the field named by `dependsOn` (default
	// "district_id"); Gram Panchayat/Village list scoped by `dependsOn`
	// (default "block_id").
	| "block"
	| "gram_panchayat"
	// A PDF/image attachment, uploaded immediately on choose (e.g. "Attach
	// the list of gap filling activities").
	| "file"
	// A single numbered question with several lettered (a/b/c…) sub-fields
	// laid out inline (e.g. "7. Number of toilets" — a. For Boys / b. For
	// Girls / c. Total). Only `groupFields` is used; `options` etc. are
	// ignored. Sub-fields support "text"/"number"/"date"/"select"/"checkbox"
	// only (see "checkbox" below).
	| "group"
	// Only valid as a `group`'s groupField: a real checkbox (not a Yes/No
	// select) — checking it reveals its paired `countField`'s own input
	// right underneath (e.g. "CC" checkbox → "No. of CC Roads" number box).
	| "checkbox";

export interface DsjeFieldConfig {
	name: string;
	label: string;
	type: DsjeFieldType;
	required?: boolean;
	placeholder?: string;
	options?: string[];
	// When set, this field's value is auto-calculated as the average of the
	// named fields and rendered read-only (e.g. a 3-year average).
	computeFrom?: string[];
	// Pre-filled value used when there's no saved/initial data yet (e.g. a
	// fixed "Tamil Nadu" for a State field).
	defaultValue?: string;
	// Always disabled, regardless of view/edit mode (e.g. a fixed State field).
	readOnly?: boolean;
	// For an "institute" field: the name of the sibling field holding the
	// district id to scope the institute list by. Defaults to "district_id".
	dependsOn?: string;
	// For a "group" field: its lettered (a/b/c…) sub-fields.
	groupFields?: DsjeFieldConfig[];
	// For a "group" field: an optional extra box shown first, unlettered,
	// right under the question (e.g. a Yes/No gate before the lettered
	// a/b/c/d count breakdown).
	primaryField?: DsjeFieldConfig;
	// For a "checkbox" groupField: the count field revealed right
	// underneath once this checkbox is checked (e.g. "No. of CC Roads").
	countField?: DsjeFieldConfig;
	// For a "file" field: the exact `documents.code` row this upload must
	// match on the backend (uploadController 404s with "Invalid document"
	// otherwise). Every "file" field needs its own pre-created documents row.
	uploadCode?: string;
}

export interface DsjeSectionConfig {
	title: string;
	columns?: 1 | 2 | 3 | 4;
	fields: DsjeFieldConfig[];
	// "grid" (default): the numbered field-grid layout.
	// "table": a "Question/Parameter | Yes | No | Remark" table — each
	// "yesno" field becomes one row with Yes/No radio columns and an
	// auto-added Remark column (stored as `${field.name}_remark`); a
	// non-"yesno" field in a table section renders as a single wide-value
	// row instead (no Yes/No columns), for the odd numeric/text row mixed
	// into an otherwise yes/no table (e.g. a bed-capacity count).
	// "paired": a "Documents and Registers | Availability" row list —
	// `fields` must alternate [question, remark, question, remark, ...];
	// each pair renders as one unnumbered zebra-striped row: the question's
	// own label and input on the left, the remark as a plain textarea on
	// the right.
	layout?: "grid" | "table" | "paired";
	// When set, this section is a repeatable list of rows (e.g. a Staffing
	// Pattern table with an "Add" button) instead of a fixed field set.
	// `fields` above is ignored; each row is built from these field configs,
	// and the row list is stored as an array under this name.
	repeating?: {
		arrayName: string;
		rowFields: DsjeFieldConfig[];
	};
	// When set, this section is a single card grouping several named
	// sub-groups (e.g. "Institution Checklists" containing its own
	// Infrastructure/Transparency/Entitlements of Residents sub-tables), for
	// a reference format that nests checklists under one umbrella heading.
	// `fields`/`layout`/`columns` above are ignored. A subsection may reuse
	// the exact same field `name`s already used elsewhere in the form (the
	// reference itself repeats some questions under this heading) — Formik
	// keeps both places in sync since they're the same field.
	subsections?: {
		title: string;
		layout?: "grid" | "table";
		columns?: 1 | 2 | 3 | 4;
		fields: DsjeFieldConfig[];
	}[];
	// When set, this section is a "Class | value(s)" table — one row per
	// class (e.g. 9th/10th/12th), each row holding one or more small
	// sub-fields (e.g. Girls/Boys/Total) laid out side by side. A row with a
	// single field shows just its input (no mini-label); more than one
	// field shows each with its own inline "Label:" prefix.
	// `fields`/`layout`/`columns` above are ignored.
	classTable?: {
		rowLabelHeader: string;
		valueHeader: string;
		rows: { label: string; fields: DsjeFieldConfig[] }[];
	};
	// A "Sr.No | Records | Yes | No | Updated | Not Updated" table — each
	// record gets two independent Yes/No radio pairs (existence, stored as
	// `${name}_status`, and currency, stored as `${name}_currency`).
	// `extraFields` (optional) render as a normal grid below the table,
	// inside the same card (e.g. free-text suggestions/remarks).
	// `fields`/`layout`/`columns` above are ignored.
	maintenanceTable?: {
		records: { name: string; label: string }[];
		extraFields?: DsjeFieldConfig[];
	};
}

export interface DsjeSubSchemeFormConfig {
	// Must match the `name` seeded in social-audit-api/sql/2026_07_add_sub_schemes.sql
	matchName: string;
	description: string;
	colorHex: string;
	// Single long scrolling page with one final Submit — the default for
	// every existing sub-scheme form. Ignored (and may be left empty: [])
	// when `tabs` is set instead.
	sections: DsjeSectionConfig[];
	// When set, this sub-scheme's form is a tabbed, multi-step wizard
	// instead (e.g. "Basic Details of Village | Need Assessment... | Works
	// Related | Developmental Monitorable Indicators") — each tab's
	// "Submit & Next" saves progress immediately (create on the first tab,
	// update thereafter, exactly like NMP/CMBFS's stepper), and the last
	// tab's button reads "Submit". `sections` above is ignored.
	tabs?: {
		title: string;
		sections: DsjeSectionConfig[];
	}[];
}

export const getDsjeFieldCount = (config: DsjeSubSchemeFormConfig): number =>
	config.sections.reduce((sum, section) => sum + section.fields.length, 0);

// "2010" through next year (so the dropdown always includes the year ahead
// of whatever year it currently is), newest first.
const yearOptions = (startYear = 2010): string[] => {
	const endYear = new Date().getFullYear() + 1;
	const years: string[] = [];
	for (let y = endYear; y >= startYear; y--) years.push(String(y));
	return years;
};

// Canonical display order for the DSJE sub-scheme list (matches the
// reference design docs/Scheme and Report/dsje_complete_system_1.html)
export const getDsjeSubSchemeOrder = (name: string): number => {
	const index = dsjeSubSchemeFormConfigs.findIndex(
		(c) => c.matchName === name,
	);
	return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

const CONSTRUCTION_STATUS_OPTIONS = [
	"Construction completed",
	"Completed but not handed over",
	"Under construction",
	"Not yet started",
];

export const dsjeSubSchemeFormConfigs: DsjeSubSchemeFormConfig[] = [
	{
		matchName: "Hostels under PMAJAY",
		description: "Hostel (BJRC) — construction, occupancy, facilities, fees",
		colorHex: "#4a6cf7",
		sections: [
			{
				title: "Hostel identification",
				columns: 2,
				fields: [
					{ name: "hostel_name", label: "Name of hostel", type: "text", required: true, placeholder: "Enter hostel name" },
					{ name: "hostel_type", label: "Type of hostel", type: "select", required: true, options: ["Boys", "Girls"] },
					{ name: "sanction_year", label: "Sanction year", type: "number", required: true, placeholder: "e.g. 2021" },
					{ name: "construction_status", label: "Construction status", type: "select", required: true, options: CONSTRUCTION_STATUS_OPTIONS },
					{ name: "is_operational", label: "Is hostel operational?", type: "yesno" },
				],
			},
			{
				title: "Occupancy",
				columns: 4,
				fields: [
					{ name: "seats_sanctioned", label: "Total seats sanctioned", type: "number" },
					{ name: "students_records", label: "Students as per records", type: "number" },
					{ name: "students_verified", label: "Students as per verification", type: "number" },
					{ name: "enrollment_year1", label: "Enrolment — Year 1", type: "number", placeholder: "e.g. 2022" },
					{ name: "enrollment_year2", label: "Enrolment — Year 2", type: "number", placeholder: "e.g. 2023" },
					{ name: "enrollment_year3", label: "Enrolment — Year 3", type: "number", placeholder: "e.g. 2024" },
					{
						name: "avg_enrollment_3yr",
						label: "Avg enrolment (last 3 yrs)",
						type: "number",
						computeFrom: ["enrollment_year1", "enrollment_year2", "enrollment_year3"],
					},
				],
			},
			{
				title: "Fees",
				columns: 2,
				fields: [
					{ name: "accommodation_fee", label: "Accommodation fee charged?", type: "yesno" },
					{ name: "food_fee", label: "Food fee charged?", type: "yesno" },
				],
			},
			{
				title: "Facilities & security",
				columns: 3,
				fields: [
					{ name: "facility_cots", label: "Cots available?", type: "yesno" },
					{ name: "facility_tables", label: "Tables available?", type: "yesno" },
					{ name: "facility_chairs", label: "Chairs available?", type: "yesno" },
					{ name: "warden_available", label: "Warden availability?", type: "yesno" },
					{ name: "warden_separate_room", label: "Separate warden room?", type: "yesno" },
					{ name: "security_guard", label: "Security guard?", type: "yesno" },
					{ name: "watchman_room", label: "Separate watchman room?", type: "yesno" },
					{ name: "cctv_installed", label: "CCTV installed?", type: "yesno" },
					{ name: "mosje_display_board", label: "MoSJE display board?", type: "yesno" },
				],
			},
			{
				title: "Impact",
				columns: 1,
				fields: [
					{ name: "impact_remarks", label: "Impact of social audit, if any", type: "textarea", placeholder: "Describe any impact observed…" },
				],
			},
		],
	},
	{
		matchName: "Hostels (BJRC) under PMAJAY",
		description: "Full Construction of Hostel format — sanction, construction, facilities, kitchen, document verification",
		colorHex: "#0e7490",
		sections: [
			{
				title: "Basic Details",
				columns: 4,
				fields: [
					{ name: "financial_year", label: "Financial Year", type: "financial_year", required: true },
					{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
					{ name: "district_id", label: "District", type: "district", required: true },
					{ name: "institute_id", label: "Institute", type: "institute", required: true, dependsOn: "district_id" },
					{ name: "social_audit_date_from", label: "Date of Social Audit (From)", type: "date", required: true },
					{ name: "social_audit_date_to", label: "Date of Social Audit (To)", type: "date", required: true },
					{ name: "hostel_name", label: "Name of the Hostel", type: "text", required: true },
					{ name: "hostel_address", label: "Address of the hostel", type: "text" },
					{ name: "hostel_nature", label: "Nature of Hostel", type: "select", options: ["Boys", "Girls"] },
					{ name: "implementing_agency", label: "Implementing Agency", type: "text" },
					{ name: "institute_name", label: "Institute Name", type: "text" },
					{ name: "low_sc_female_literacy_area", label: "Is the hostel located in an area having low SC female literacy? (Only in case of girl's hostel)", type: "select", options: ["Yes", "No"] },
					{ name: "avg_distance_hostel_institute", label: "What is the average distance between the hostel and educational institutes?", type: "text" },
				],
			},
			{
				title: "Seats & Enrollment",
				columns: 4,
				fields: [
					{ name: "total_seats_sanctioned", label: "Total number of seats sanctioned", type: "number" },
					{ name: "seats_records_sc_boys", label: "Seats sanctioned (as per records) — SC Boys", type: "number" },
					{ name: "seats_records_sc_girls", label: "Seats sanctioned (as per records) — SC Girls", type: "number" },
					{ name: "seats_verification_sc_boys", label: "Seats sanctioned (as per verification) — SC Boys", type: "number" },
					{ name: "seats_verification_other_boys", label: "Seats sanctioned (as per verification) — Other Boys", type: "number" },
					{ name: "seats_verification_sc_girls", label: "Seats sanctioned (as per verification) — SC Girls", type: "number" },
					{ name: "seats_verification_other_girls", label: "Seats sanctioned (as per verification) — Other Girls", type: "number" },
					{ name: "enrollment_year1", label: "Year wise enrollment (Last 3 years) — Year 1", type: "select", options: yearOptions() },
					{ name: "enrollment_year1_count", label: "Total number of students enrolled — Year 1", type: "number" },
					{ name: "enrollment_year2", label: "Year wise enrollment (Last 3 years) — Year 2", type: "select", options: yearOptions() },
					{ name: "enrollment_year2_count", label: "Total number of students enrolled — Year 2", type: "number" },
					{ name: "enrollment_year3", label: "Year wise enrollment (Last 3 years) — Year 3", type: "select", options: yearOptions() },
					{ name: "enrollment_year3_count", label: "Total number of students enrolled — Year 3", type: "number" },
				],
			},
			{
				title: "Sanction Details",
				columns: 4,
				fields: [
					{ name: "seats_proposed_in_hostel", label: "No. of seats proposed in the hostel", type: "number" },
					{ name: "sanction_date", label: "Sanction Date", type: "date" },
					{ name: "sanction_amount_centre", label: "Sanction Amount from Centre", type: "number" },
					{ name: "sanction_amount_state", label: "Sanction Amount from State", type: "number" },
					{ name: "construction_commencement_date", label: "Construction commencement date", type: "date" },
					{ name: "expenditure_till_audit_date", label: "Expenditure till date of social audit", type: "number" },
					{ name: "hostel_status", label: "Status of Hostel", type: "select", options: ["Completed", "In Progress", "Yet to start the work"] },
					{ name: "completion_date_if_completed", label: "In case of completed, date of completion of construction", type: "date" },
					{ name: "delay_reasons", label: "Reasons if there is any delay", type: "textarea" },
				],
			},
			{
				title: "Construction Related",
				columns: 4,
				fields: [
					{ name: "total_floors", label: "Total floors in hostels", type: "number" },
					{ name: "total_rooms_available", label: "Total rooms available in hostels", type: "number" },
					{ name: "special_room_warden", label: "Is there a special room for the warden?", type: "select", options: ["Yes", "No"] },
					{ name: "special_room_watchmen", label: "Is there a special room for the watchmen?", type: "select", options: ["Yes", "No"] },
					{ name: "ramps_constructed", label: "Are the ramps constructed in the building?", type: "select", options: ["Yes", "No"] },
					{ name: "total_toilets", label: "Total number of toilets", type: "number" },
					{ name: "total_bathrooms", label: "Total number of bathrooms", type: "number" },
					{ name: "cctv_installed", label: "Are CCTVs installed in the Hostel?", type: "select", options: ["Yes", "No", "Install But Not Functional"] },
				],
			},
			{
				title: "Other Facilities for Students",
				columns: 4,
				fields: [
					{ name: "first_aid_available", label: "Is first aid facility available for students?", type: "select", options: ["Yes", "No"] },
					{ name: "health_camps_conducted", label: "How many health camps have been conducted so far?", type: "number" },
					{ name: "computer_facilities", label: "Does the hostel have computer facilities for students?", type: "select", options: ["Yes", "No"] },
					{ name: "sport_coaching_available", label: "Is sport coaching available?", type: "select", options: ["Yes", "No"] },
					{ name: "coaching_tuition_available", label: "Is there coaching or tuition facilities available for Student in the hostel?", type: "select", options: ["Yes", "No"] },
					{ name: "complaint_box_available", label: "Is there a complaint box available?", type: "select", options: ["Yes", "No"] },
					{ name: "sanitary_napkin_service", label: "Is there a sanitary napkin service available for girls in the hostel?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Accommodation Related",
				columns: 3,
				fields: [
					{ name: "students_per_room", label: "Number of students staying in one room", type: "number" },
					{ name: "special_room_allocated_student", label: "Is there any special room allocated to any student?", type: "select", options: ["Yes", "No"] },
					{ name: "facility_cots_records", label: "Facilities as per record — Cots", type: "number" },
					{ name: "facility_table_records", label: "Facilities as per record — Table", type: "number" },
					{ name: "facility_chairs_records", label: "Facilities as per record — Chairs", type: "number" },
					{ name: "facility_cots_verification", label: "Facilities as per verification — Cots", type: "number" },
					{ name: "facility_table_verification", label: "Facilities as per verification — Table", type: "number" },
					{ name: "facility_chairs_verification", label: "Facilities as per verification — Chairs", type: "number" },
					{ name: "warden_available", label: "Is there a warden available in the hostel?", type: "select", options: ["Yes", "No"] },
					{ name: "warden_available_24x7", label: "Is the warden available for 24 X 7?", type: "select", options: ["Yes", "No"] },
					{ name: "watchman_available", label: "Is there watchman/woman watchman available in the hostel?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Kitchen Related",
				columns: 4,
				fields: [
					{ name: "separate_kitchen_room", label: "Is there a separate kitchen room available?", type: "select", options: ["Yes", "No"] },
					{ name: "kitchen_hygienic", label: "Is the kitchen in hygienic condition?", type: "select", options: ["Yes", "No"] },
					{ name: "food_served_on_time", label: "Is the food being served on time?", type: "select", options: ["Yes", "No"] },
					{ name: "food_quality_nutritional", label: "How is the quality of food served in terms of nutritional value?", type: "select", options: ["Very Good", "Good", "Ok", "Poor", "Very Poor"] },
					{ name: "food_quantity_sufficient", label: "Is quantity of food served is sufficient for students?", type: "select", options: ["Sufficient", "Not sufficient"] },
					{ name: "drinking_water_available", label: "Is clean drinking water facility available in the hostel?", type: "select", options: ["Yes", "No"] },
					{ name: "hostel_benefiting_education", label: "Is the hostel facility benefiting the students for their education?", type: "select", options: ["Yes", "No"] },
					{ name: "benefited_district", label: "Districts from which students are getting benefited", type: "text" },
					{ name: "benefited_block", label: "Blocks from which students are getting benefited", type: "text" },
					{ name: "charging_fee_from_students", label: "Whether the organization is charging any fee/contribution from students?", type: "select", options: ["Yes", "No"] },
					{ name: "any_other_issue", label: "Any Other Issue", type: "text" },
					{ name: "any_support_required", label: "Any support required?", type: "text" },
					{ name: "fee_monthly_charges", label: "If yes — Monthly charges", type: "number" },
					{ name: "fee_annual_charges", label: "If yes — Annual charges", type: "number" },
					{ name: "user_charges_reflected_accounts", label: "Whether the user charges collected are reflected in the account of the organization", type: "select", options: ["Yes", "No"] },
					{ name: "admission_process", label: "What is the admission process for hostel", type: "text" },
					{ name: "have_caste_certificates", label: "Do they have caste certificates of students", type: "select", options: ["Yes", "No"] },
					{ name: "have_income_certificates", label: "Do they have income certificates of students", type: "select", options: ["Yes", "No"] },
					{ name: "advertisement_strategies", label: "Strategies adopted for advertisement of hostel", type: "text" },
				],
			},
			{
				title: "Document Verification",
				columns: 4,
				fields: [
					{ name: "doc_administrative_sanction", label: "Administrative Sanction", type: "select", options: ["Yes", "No"] },
					{ name: "doc_work_commencement_order", label: "Work Commencement Order", type: "select", options: ["Yes", "No"] },
					{ name: "doc_work_completion_certificate", label: "Work Completion Certificate", type: "select", options: ["Yes", "No"] },
					{ name: "doc_soil_test_report", label: "Soil Test Report", type: "select", options: ["Yes", "No"] },
					{ name: "doc_inspection_report", label: "Inspection Report", type: "select", options: ["Yes", "No"] },
					{ name: "doc_technical_sanction", label: "Technical Sanction", type: "select", options: ["Yes", "No"] },
					{ name: "doc_bills_vouchers", label: "Bills & Vouchers", type: "select", options: ["Yes", "No"] },
					{ name: "doc_photos_of_works", label: "Photos of Works", type: "select", options: ["Yes", "No"] },
					{ name: "doc_layout_of_hostel", label: "Layout of Hostel", type: "select", options: ["Yes", "No"] },
					{ name: "doc_completion_certificate", label: "Completion Certificate", type: "select", options: ["Yes", "No"] },
					{ name: "social_audit_team_remarks", label: "Remarks by Social Audit Team", type: "text" },
				],
			},
			{
				title: "Social Audit Team Members",
				fields: [],
				repeating: {
					arrayName: "social_audit_team_members",
					rowFields: [
						{ name: "member_name", label: "Name", type: "text" },
						{ name: "member_position", label: "Position / Role", type: "text" },
						{ name: "member_mobile_no", label: "Mobile No", type: "text" },
					],
				},
			},
		],
	},
	{
		matchName: "Villages under PMAJAY",
		description: "Social Audit Format for PMAJAY — Adarsh Gram",
		colorHex: "#16a34a",
		sections: [],
		tabs: [
			{
				title: "Basic Details of Village",
				sections: [
					{
						title: "Basic Details of Village",
						columns: 4,
						fields: [
							{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
							{ name: "district_id", label: "District", type: "district", required: true },
							{ name: "block_id", label: "Block", type: "block", required: true, dependsOn: "district_id" },
							{ name: "gram_panchayat_id", label: "Village", type: "gram_panchayat", required: true, dependsOn: "block_id" },
							{
								name: "social_audit_date_group",
								label: "Date of Social Audit",
								type: "group",
								groupFields: [
									{ name: "social_audit_date_from", label: "From", type: "date" },
									{ name: "social_audit_date_to", label: "To", type: "date" },
								],
							},
							{ name: "financial_year", label: "Financial Year", type: "financial_year", required: true },
							{ name: "sc_population_of_village", label: "SC Population of Village", type: "number" },
							{ name: "rank_of_village", label: "Rank of Village", type: "text" },
							{ name: "amount_received_pmajay_central", label: "Amount Received (PMAJAY-Central Schemes)", type: "number" },
							{ name: "amount_spent_pmajay_central", label: "Amount Spent (PMAJAY-Central Schemes)", type: "number" },
						],
					},
				],
			},
			{
				title: "Need Assessment & Village Development Plan",
				sections: [
					{
						title: "Need Assessment & Village Development Plan",
						columns: 4,
						fields: [
							{ name: "survey_conducted_by", label: "Who carried out the survey for need assessment?", type: "select", options: ["Panchayat Sachiv", "Mukhiya", "Asha Workers", "Pradhan", "Upradhan", "Others", "Not Applicable"] },
							{ name: "training_given_to_members", label: "Was any training given to the members who were involved in carrying out the \"Need Assessment\"?", type: "select", options: ["Yes", "No"] },
							{ name: "vdp_prepared_based_on_need_assessment", label: "Whether VDP is prepared based on Need Assessment?", type: "select", options: ["Yes", "No"] },
							{ name: "family_individual_needs_captured", label: "Whether Family/Individual needs are captured in VDP?", type: "select", options: ["Yes", "No"] },
							{ name: "infrastructure_needs_captured", label: "Whether infrastructure needs are captured in the VDP?", type: "select", options: ["Yes", "No"] },
							{ name: "vlcc_formed_as_per_rules", label: "Is village level convergence committee (VLCC) formed as per the rules?", type: "select", options: ["Yes", "No"] },
							{ name: "vdp_approved_by_gram_sabha", label: "Whether VDP is approved by the Gram Sabha?", type: "select", options: ["Yes", "No"] },
							{ name: "vdp_part_of_gp_development_plan", label: "Whether VDP is part of Gram Panchayat Development Plan?", type: "select", options: ["Yes", "No"] },
							{
								name: "vdp_approval_district_committee_group",
								label: "Date of approval of VDP by District Level Committee",
								type: "group",
								groupFields: [
									{ name: "vdp_district_approval_status", label: "Approval", type: "select", options: ["Approved", "Not Approved"] },
									{ name: "vdp_district_approval_date", label: "Date of approval", type: "date" },
								],
							},
							{ name: "vlcc_aware_roles_responsibilities", label: "Is village Level VLCC aware of their roles and responsibilities?", type: "select", options: ["Yes", "No"] },
							{ name: "format_v_updated_monthly", label: "Whether Format-V is updated on monthly basis?", type: "select", options: ["Yes", "No", "Not submitted"] },
						],
					},
				],
			},
			{
				title: "Works Related",
				sections: [
					{
						title: "Works Related",
						columns: 3,
						fields: [
							{ name: "works_identified_in_vdp", label: "Total number of works identified in the VDP", type: "number" },
							{ name: "works_identified_gap_filling", label: "Total number of works identified for gap filling activities under the scheme", type: "number" },
							{ name: "gap_filling_activities_list_file", label: "Attach the list of gap filling activities", type: "file", uploadCode: "DOSJE-village- filling-activities" },
							{ name: "works_in_progress_gap_filling", label: "Number of works in progress (From Gap Filling Activities)", type: "number" },
							{ name: "works_completed_gap_filling", label: "Number of works completed (From Gap Filling Activities)", type: "number" },
							{ name: "type_of_works_undertaken", label: "Type of works undertaken? (From Gap Filling Activities)", type: "textarea" },
							{ name: "works_benefit_sc_community", label: "Do the works undertaken benefit the development of the SC community?", type: "select", options: ["Yes", "No"] },
							{ name: "works_useful_for_community_count", label: "Write the number of works useful for the community", type: "number" },
						],
					},
				],
			},
			{
				title: "Developmental Monitorable Indicators",
				sections: [
					{
						title: "Drinking Water and Sanitation",
						columns: 4,
						fields: [
							{ name: "drinking_water_sources_adequate", label: "Whether adequate sustainable drinking water sources to cover the village are available? (Yes/No)", type: "select", options: ["Yes", "No"] },
							{ name: "hh_provided_clean_drinking_water", label: "No. of households provided with clean drinking water", type: "number" },
							{ name: "hh_not_provided_clean_drinking_water", label: "No. of households not provided with clean drinking water (As per verification during social audit)", type: "number" },
							{ name: "hh_individual_household_latrines", label: "No. of households having individual household latrines", type: "number" },
							{ name: "hh_not_having_individual_latrines", label: "No. of households not having individual household latrines (Out of total verified in SA)", type: "number" },
							{
								name: "school_toilets_group",
								label: "Have toilets been provided in all schools in the village?",
								type: "group",
								groupFields: [
									{ name: "school_toilets_provided", label: "Provided (Yes/No)", type: "select", options: ["Yes", "No"] },
									{ name: "total_schools_in_village", label: "Total Schools in Village", type: "number" },
									{ name: "schools_having_toilets", label: "No. of schools having toilets", type: "number" },
								],
							},
							{
								name: "anganwadi_toilets_group",
								label: "Have toilets been provided in all Anganwadis in the village? (Yes/No)",
								type: "group",
								groupFields: [
									{ name: "anganwadi_toilets_provided", label: "Provided (Yes/No)", type: "select", options: ["Yes", "No"] },
									{ name: "total_anganwadis_in_village", label: "Total Anganwadis in Village", type: "number" },
									{ name: "anganwadis_having_toilets", label: "No. of Anganwadis having toilets", type: "number" },
								],
							},
							{ name: "open_defecation_still_present", label: "Whether people are still defecating in the open in the village? (Yes/No)", type: "select", options: ["Yes", "No"] },
							{
								name: "internal_roads_drains_group",
								label: "Are Drains available in all internal roads?",
								type: "group",
								groupFields: [
									{ name: "internal_roads_drains_available", label: "Available (Yes/No)", type: "select", options: ["Yes", "No"] },
									{ name: "no_of_roads", label: "No. of Roads", type: "number" },
									{ name: "availability_of_drains", label: "Availability of Drains", type: "number" },
								],
							},
							{
								name: "drain_functioning_group",
								label: "No. of drain functioning and not functioning",
								type: "group",
								groupFields: [
									{ name: "drain_functioning", label: "Functioning", type: "number" },
									{ name: "drain_not_functioning", label: "Not functioning", type: "number" },
								],
							},
							{ name: "solid_waste_management_system", label: "Is there solid waste management system in village?", type: "select", options: ["Yes", "No"] },
						],
					},
					{
						title: "Education",
						columns: 4,
						fields: [
							{
								name: "education_primary_school_group",
								label: "No. of children (6-10 years) attending primary school",
								type: "group",
								primaryField: { name: "primary_children_present", label: "No. of Children", type: "number" },
								groupFields: [
									{ name: "primary_total_children", label: "Total Children", type: "number" },
									{ name: "primary_attending", label: "Attending", type: "number" },
									{ name: "primary_drop_out", label: "Drop Out", type: "number" },
									{ name: "primary_not_attending", label: "Not attending", type: "number" },
								],
							},
							{
								name: "education_middle_school_group",
								label: "No. of children (11-13 years) attending middle school",
								type: "group",
								primaryField: { name: "middle_children_present", label: "No. of Children", type: "number" },
								groupFields: [
									{ name: "middle_total_children", label: "Total Children", type: "number" },
									{ name: "middle_attending", label: "Attending", type: "number" },
									{ name: "middle_drop_out", label: "Drop Out", type: "number" },
									{ name: "middle_not_attending", label: "Not attending", type: "number" },
								],
							},
							{
								name: "education_secondary_school_group",
								label: "No. of children (14-15 years) attending secondary school",
								type: "group",
								primaryField: { name: "secondary_children_present", label: "No. of Children", type: "number" },
								groupFields: [
									{ name: "secondary_total_children", label: "Total Children", type: "number" },
									{ name: "secondary_attending", label: "Attending", type: "number" },
									{ name: "secondary_drop_out", label: "Drop Out", type: "number" },
									{ name: "secondary_not_attending", label: "Not attending", type: "number" },
								],
							},
							{
								name: "education_higher_secondary_school_group",
								label: "No. of children (16-17 years) attending higher secondary school",
								type: "group",
								primaryField: { name: "higher_secondary_children_present", label: "No. of Children", type: "number" },
								groupFields: [
									{ name: "higher_secondary_total_children", label: "Total Children", type: "number" },
									{ name: "higher_secondary_attending", label: "Attending", type: "number" },
									{ name: "higher_secondary_drop_out", label: "Drop Out", type: "number" },
									{ name: "higher_secondary_not_attending", label: "Not attending", type: "number" },
								],
							},
							{
								name: "education_higher_education_group",
								label: "No. of children (18-23 years) attending higher secondary education",
								type: "group",
								primaryField: { name: "higher_education_children_present", label: "No. of Children", type: "number" },
								groupFields: [
									{ name: "higher_education_total_children", label: "Total Children", type: "number" },
									{ name: "higher_education_attending", label: "Attending", type: "number" },
									{ name: "higher_education_drop_out", label: "Drop Out", type: "number" },
									{ name: "higher_education_not_attending", label: "Not attending", type: "number" },
								],
							},
							{
								name: "prematric_scholarship_group",
								label: "No. of SC Children receiving pre-matric scholarship",
								type: "group",
								groupFields: [
									{ name: "prematric_total_eligible", label: "Total Eligible Children", type: "number" },
									{ name: "prematric_total_receiving", label: "Total receiving", type: "number" },
									{ name: "prematric_not_receiving", label: "Not Receiving", type: "number" },
								],
							},
							{
								name: "postmatric_scholarship_group",
								label: "No. of SC children receiving post-matric scholarship",
								type: "group",
								groupFields: [
									{ name: "postmatric_total_eligible", label: "Total Eligible Children", type: "number" },
									{ name: "postmatric_total_receiving", label: "Total receiving", type: "number" },
									{ name: "postmatric_not_receiving", label: "Not Receiving", type: "number" },
								],
							},
						],
					},
					{
						title: "Health and Nutrition",
						columns: 4,
						fields: [
							{
								name: "health_protection_scheme_group",
								label: "No. of households covered under any health protection scheme (out of total verified in Social Audit)",
								type: "group",
								groupFields: [
									{ name: "health_total_verified_sa", label: "Total verified in SA", type: "number" },
									{ name: "health_covered", label: "Covered", type: "number" },
									{ name: "health_not_covered", label: "Not Covered", type: "number" },
								],
							},
							{ name: "ambulance_facility_on_call", label: "Is an emergency Ambulance facility available on call?", type: "select", options: ["Yes", "No"] },
							{ name: "women_severely_anemic", label: "No. of women who are severely anemic", type: "number" },
							{ name: "institutional_deliveries_last_year", label: "No. of institutional deliveries in the village during last one year", type: "number" },
							{ name: "low_birth_weight_children_last_year", label: "No. of low-birth weight children born during the last one year", type: "number" },
							{ name: "children_full_immunization", label: "No. of full immunization of children", type: "number" },
							{ name: "children_not_immunized", label: "No. of children not immunized", type: "number" },
							{ name: "underweight_children_0_5_years", label: "No. of underweight children (0-5 years) in the village", type: "number" },
							{ name: "pregnant_women_died_last_year", label: "How many pregnant women died during the last one year?", type: "number" },
							{ name: "children_died_last_year", label: "How many children died during the last one year?", type: "number" },
							{ name: "communicable_diseases_treated", label: "No. of persons with communicable diseases getting treatment as per protocols", type: "number" },
							{ name: "communicable_diseases_not_treated", label: "No. of persons with communicable diseases not getting treatment as per protocols", type: "number" },
							{ name: "anganwadis_to_be_constructed", label: "Total number of Anganwadis to be constructed?", type: "number" },
							{ name: "anganwadis_constructed_in_village", label: "Total Anganwadis constructed in the village?", type: "number" },
						],
					},
					{
						title: "Social Security",
						columns: 3,
						fields: [
							{ name: "women_getting_widow_pension", label: "No. of eligible women getting widow pension", type: "number" },
							{ name: "women_not_getting_widow_pension", label: "No. of eligible women not getting widow pension found during social audit (Attach the list)", type: "number" },
							{ name: "persons_getting_old_age_pension", label: "No. of eligible persons getting old age pensions", type: "number" },
							{ name: "persons_not_getting_old_age_pension", label: "No. of eligible persons not getting old age pensions found during social audit (Attach the list)", type: "number" },
							{ name: "persons_getting_disability_pension", label: "No. of eligible persons getting disability pension", type: "number" },
							{ name: "persons_not_getting_disability_pension", label: "No. of eligible persons not getting disability pension found during social audit", type: "number" },
						],
					},
					{
						title: "Rural Roads and Housing",
						columns: 3,
						fields: [
							{ name: "village_connected_all_weather_roads", label: "Is the village connected by all-weather roads? (Yes/No)", type: "select", options: ["Yes", "No"] },
							{
								name: "road_types_group",
								label: "Types of roads that are CC/Brick top/Pakka/Tiled? (Select multiple)",
								type: "group",
								groupFields: [
									{
										name: "road_type_cc",
										label: "CC",
										type: "checkbox",
										countField: { name: "road_type_cc_count", label: "No. of CC Roads", type: "number" },
									},
									{
										name: "road_type_brick_top",
										label: "Brick top",
										type: "checkbox",
										countField: { name: "road_type_brick_top_count", label: "No. of Brick top Roads", type: "number" },
									},
									{
										name: "road_type_pakka",
										label: "Pakka",
										type: "checkbox",
										countField: { name: "road_type_pakka_count", label: "No. of Pakka Roads", type: "number" },
									},
									{
										name: "road_type_tiled",
										label: "Tiled",
										type: "checkbox",
										countField: { name: "road_type_tiled_count", label: "No. of Tiled Roads", type: "number" },
									},
								],
							},
							{ name: "households_kacha_unsafe_houses", label: "No. of households living in Kacha/unsafe houses", type: "number" },
						],
					},
					{
						title: "Electricity and Clean Fuel",
						columns: 4,
						fields: [
							{ name: "village_electrified", label: "Whether the village has been electrified?", type: "select", options: ["Yes", "No"] },
							{ name: "hh_not_having_electricity", label: "No. of households not having electricity connection (As per no. of HH verified in Social Audit)", type: "number" },
							{ name: "hh_not_having_gas_connection", label: "No. of households not having gas connection (As per verified HH in Social Audit)", type: "number" },
							{ name: "internal_roads_with_street_lights", label: "No. of internal roads having street lights", type: "number" },
						],
					},
					{
						title: "Agriculture Practices etc",
						columns: 3,
						fields: [
							{ name: "farmers_provided_soil_health_card", label: "No. of eligible farmers provided with Soil Health Card", type: "number" },
							{ name: "farmers_not_provided_soil_health_card", label: "No. of eligible farmers not provided with Soil Health Card (As per verification during social audit)", type: "number" },
							{
								name: "organic_farming_group",
								label: "No. of farmers adopted organic farming practices (As per verification during social audit focus group discussion)",
								type: "group",
								groupFields: [
									{ name: "organic_farming_total_farmers", label: "Total Farmers", type: "number" },
									{ name: "organic_farming_adopted", label: "Adopted", type: "number" },
								],
							},
							{
								name: "watershed_management_group",
								label: "No. of households adopted watershed management practices (As per verification during social audit focus group discussion)",
								type: "group",
								groupFields: [
									{ name: "watershed_total_hh", label: "Total HH", type: "number" },
									{ name: "watershed_adopted", label: "Adopted", type: "number" },
								],
							},
						],
					},
					{
						title: "Financial Inclusion",
						columns: 3,
						fields: [
							{ name: "persons_not_having_aadhaar", label: "No. of persons not having Aadhaar Card as verified during social audit", type: "number" },
							{ name: "hh_members_not_having_bank_accounts", label: "No. of household members (above 18 years) not having accounts in the Bank/post office", type: "number" },
							{ name: "persons_covered_pm_suraksha_bima", label: "No. of eligible persons covered under Pradhan Mantri Suraksha Bima Yojana", type: "number" },
							{ name: "persons_not_covered_pm_suraksha_bima", label: "No. of eligible persons not covered under Pradhan Mantri Suraksha Bima Yojana (Out of total verified during social audit)", type: "number" },
							{ name: "persons_covered_pm_jeevan_bima", label: "Person covered under Pradhan Mantri Jeevan Bima Yojana", type: "number" },
							{ name: "persons_not_covered_pm_jeevan_bima", label: "Eligible person not covered under Pradhan Mantri Jeevan Bima Yojana (Out of total verified during social audit)", type: "number" },
						],
					},
					{
						title: "Digitization",
						columns: 2,
						fields: [
							{ name: "village_has_internet_access", label: "Does the village have access to Internet connectivity?", type: "select", options: ["Yes", "No"] },
							{ name: "village_has_csc_or_cybercafe", label: "Does the village have a Common Service Centre or Cybercafé?", type: "select", options: ["Yes", "No"] },
						],
					},
					{
						title: "Livelihood and Skill Development",
						columns: 3,
						fields: [
							{ name: "youth_eligible_skills_training", label: "No. of eligible youth who are eligible to get skills development training", type: "number" },
							{ name: "youth_given_skills_training", label: "No. of eligible youth to whom skills development training is already given", type: "number" },
							{ name: "youth_to_be_given_skills_training", label: "No. of eligible youth to whom skills development training to be given", type: "number" },
							{ name: "youth_availed_bank_linked_loan", label: "No. of eligible youth have been able to avail bank linked loan", type: "number" },
							{ name: "youth_not_availed_bank_linked_loan", label: "No. of eligible youth who have not been able to avail bank linked loan however wish to avail now", type: "number" },
							{ name: "hh_member_of_any_shg", label: "No. of households which have at least one member as a member of any SHGs", type: "number" },
							{ name: "distance_primary_health_centre", label: "Distance of Primary Health Centre from Village", type: "text" },
							{ name: "other_gap_filling_activities_file", label: "What are the other activities that need to be covered under gap filling activities: (Attached the list)", type: "file", uploadCode: "DOSJE village- livelihood-filling-activities" },
							{ name: "rs_20_lakhs_sufficient_gap_filling", label: "Whether Rs. 20 lakhs are sufficient for Gap Filling Activities?", type: "select", options: ["Yes", "No"] },
							{ name: "social_audit_team_remarks", label: "Remarks of Social Audit Team", type: "textarea" },
						],
					},
				],
			},
		],
	},
	{
		matchName: "Hostels under YASASVI",
		description: "OBC Hostel — seats, rooms, facilities, staff",
		colorHex: "#d97706",
		sections: [
			{
				title: "Hostel details",
				columns: 2,
				fields: [
					{ name: "hostel_name", label: "Name of hostel", type: "text", required: true, placeholder: "Hostel name" },
					{ name: "hostel_type", label: "Type", type: "select", required: true, options: ["Boys", "Girls"] },
					{ name: "sanction_year", label: "Sanction year", type: "number", placeholder: "Year" },
					{ name: "construction_status", label: "Construction status", type: "select", options: CONSTRUCTION_STATUS_OPTIONS },
					{ name: "is_operational", label: "Is hostel operational?", type: "yesno" },
				],
			},
			{
				title: "Occupancy & rooms",
				columns: 4,
				fields: [
					{ name: "seats_sanctioned", label: "Total seats sanctioned", type: "number" },
					{ name: "students_records", label: "Students as per records", type: "number" },
					{ name: "students_verified", label: "Students as per verification", type: "number" },
					{ name: "total_rooms", label: "Total rooms available", type: "number" },
				],
			},
			{
				title: "Facilities, fees & staff",
				columns: 3,
				fields: [
					{ name: "facility_cots", label: "Cots?", type: "yesno" },
					{ name: "facility_tables", label: "Tables?", type: "yesno" },
					{ name: "facility_chairs", label: "Chairs?", type: "yesno" },
					{ name: "accommodation_fee", label: "Accommodation fee?", type: "yesno" },
					{ name: "food_fee", label: "Food fee?", type: "yesno" },
					{ name: "warden_available", label: "Warden available?", type: "yesno" },
					{ name: "security_guard", label: "Security guard?", type: "yesno" },
					{ name: "mosje_display_board", label: "MoSJE display board?", type: "yesno" },
				],
			},
			{
				title: "Impact",
				columns: 1,
				fields: [
					{ name: "impact_remarks", label: "Impact of social audit", type: "textarea", placeholder: "Describe impact…" },
				],
			},
		],
	},
	{
		matchName: "OBC Hostel under YASASVI",
		description: "Social Audit Format for Construction of Hostels for OBC Boys and Girls",
		colorHex: "#c2410c",
		sections: [
			{
				title: "Basic Details",
				columns: 4,
				fields: [
					{ name: "financial_year", label: "Financial year", type: "financial_year", required: true },
					{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
					{ name: "district_id", label: "District", type: "district", required: true },
					{ name: "institute_id", label: "Institute Name", type: "institute", required: true, dependsOn: "district_id" },
					{ name: "hostel_name", label: "Name of the Hostel", type: "text", required: true },
					{ name: "hostel_address", label: "Address of the hostel", type: "textarea" },
					{ name: "hostel_nature", label: "Nature of Hostel", type: "select", options: ["Girls", "Boys"] },
					{ name: "implementing_agency", label: "Implementing Agency", type: "text" },
					{ name: "distance_from_nearest_obc_hostel", label: "Distance from the nearest OBC Hostel?", type: "text" },
					{ name: "seats_capacity_of_hostel", label: "Seats capacity of hostel", type: "number" },
				],
			},
			{
				title: "Sanction Details",
				columns: 4,
				fields: [
					{ name: "seats_proposed_for_hostel", label: "No. of seats proposed for the hostel", type: "number" },
					{ name: "sanction_date", label: "Sanction Date", type: "date" },
					{ name: "sanction_amount_centre", label: "Sanction amount from Centre", type: "number" },
					{ name: "sanction_amount_state", label: "Sanction amount from State", type: "number" },
					{ name: "construction_commencement_date", label: "Construction commencement date", type: "date" },
					{ name: "expenditure_till_audit_date", label: "Expenditure till date of social audit", type: "number" },
					{ name: "hostel_status", label: "Status of Hostel", type: "select", options: ["Completed", "In Progress", "Yet to start the work"] },
					{ name: "completion_date_if_completed", label: "In case of completed, date of completion of construction", type: "date" },
					{ name: "delay_reasons", label: "Reasons if there is any delay", type: "textarea" },
				],
			},
			{
				title: "Students Details",
				columns: 4,
				fields: [
					{ name: "total_seats_sanctioned", label: "Total number of seats sanctioned", type: "number" },
					{
						name: "students_present_audit_group",
						label: "Total number of students present at the time of social audit in hostel",
						type: "group",
						groupFields: [
							{ name: "students_present_boy", label: "Boy", type: "number" },
							{ name: "students_present_girl", label: "Girl", type: "number" },
						],
					},
					{
						name: "category_wise_details_group",
						label: "Category wise Details",
						type: "group",
						groupFields: [
							{ name: "category_obc", label: "OBC", type: "number" },
							{ name: "category_dnt", label: "DNT", type: "number" },
							{ name: "category_st", label: "ST", type: "number" },
							{ name: "category_sc", label: "SC", type: "number" },
							{ name: "category_others", label: "Others", type: "number" },
						],
					},
					{ name: "students_with_disability", label: "No. of students with disability", type: "number" },
					{ name: "total_rooms_available", label: "Total number of rooms available in hostel", type: "number" },
					{ name: "building_space_adequate", label: "Is the building space adequate to run the hostel?", type: "select", options: ["Yes", "No"] },
					{ name: "non_requiring_amount_sanctioned", label: "Whether non-requiring amount sanctioned", type: "select", options: ["Yes", "No"] },
					{
						name: "students_facility_availability_group",
						label: "Availability of following for students",
						type: "group",
						groupFields: [
							{ name: "avail_no_of_students", label: "No. of Students", type: "number" },
							{ name: "avail_total_bed", label: "Total Bed", type: "number" },
							{ name: "avail_total_table", label: "Total Table", type: "number" },
							{ name: "avail_total_chairs", label: "Total Chairs", type: "number" },
							{ name: "avail_cupboard", label: "Cupboard", type: "number" },
						],
					},
				],
			},
			{
				title: "Information on Process and Procedure of selection of students during the last 3 FY years",
				fields: [],
				classTable: {
					rowLabelHeader: "",
					valueHeader: "Number of students selected during the relevant year, class-wise",
					rows: [
						{
							label: "a.",
							fields: [
								{ name: "selection_year1_financial_year", label: "Financial Year", type: "text" },
								{ name: "selection_year1_class", label: "Class", type: "text" },
								{ name: "selection_year1_students", label: "Number of Students", type: "number" },
							],
						},
						{
							label: "b.",
							fields: [
								{ name: "selection_year2_financial_year", label: "Financial Year", type: "text" },
								{ name: "selection_year2_class", label: "Class", type: "text" },
								{ name: "selection_year2_students", label: "Number of Students", type: "number" },
							],
						},
						{
							label: "c.",
							fields: [
								{ name: "selection_year3_financial_year", label: "Financial Year", type: "text" },
								{ name: "selection_year3_class", label: "Class", type: "text" },
								{ name: "selection_year3_students", label: "Number of Students", type: "number" },
							],
						},
					],
				},
			},
			{
				title: "Selection Process Details",
				columns: 3,
				fields: [
					{ name: "avg_students_last_3_years", label: "Average of number of students in last 3 years", type: "number" },
					{ name: "selection_process_brief", label: "Write brief about selection process & procedure of students", type: "textarea" },
					{ name: "caste_certificates_obtained", label: "Whether the caste certificates are obtained", type: "select", options: ["Yes", "No"] },
					{ name: "numbers_if_no", label: "Mention the Numbers if No", type: "number" },
					{ name: "students_interviewed", label: "Whether students have been interviewed", type: "select", options: ["Yes", "No"] },
					{ name: "students_enrolled_previous_year_details", label: "Details of students enrolled in previous year, out of above (Mention Year)", type: "text" },
					{ name: "passed_out_successfully", label: "Those passed out of the institutions successfully", type: "number" },
					{ name: "promoted_next_grade_same_school", label: "Promoted to next grade in the same school", type: "select", options: ["Yes", "No"] },
					{ name: "pursuing_further_studies_other_schools", label: "Pursuing further studies in other schools", type: "number" },
					{ name: "no_of_dropouts", label: "No. of drop outs", type: "number" },
					{ name: "charging_fee_from_students", label: "Whether the organization is charging any fee/contribution from students?", type: "select", options: ["Yes", "No"] },
					{
						name: "fee_details_group",
						label: "If yes",
						type: "group",
						groupFields: [
							{ name: "fee_monthly_charges", label: "Monthly charges", type: "number" },
							{ name: "fee_annual_charges", label: "Annual charges", type: "number" },
						],
					},
					{ name: "user_charges_reflected_accounts", label: "Whether the user charges collected are reflected in the account of the organization", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Essential Facilities",
				columns: 4,
				fields: [
					{ name: "facility_lighting", label: "Lighting", type: "select", options: ["Yes", "No"] },
					{ name: "facility_fans", label: "Fans", type: "select", options: ["Yes", "No"] },
					{ name: "facility_no_of_toilets", label: "No. of toilets", type: "number" },
					{ name: "facility_no_of_bathrooms", label: "No. of bathrooms", type: "number" },
					{ name: "facility_water", label: "Water facility", type: "select", options: ["Yes", "No"] },
					{ name: "facility_water_purifier", label: "Water purifier", type: "select", options: ["Yes", "No"] },
					{ name: "facility_drainage_system", label: "Drainage system", type: "select", options: ["Yes", "No"] },
					{ name: "facility_kitchen_pantry", label: "Kitchen cum pantry", type: "select", options: ["Yes", "No"] },
					{ name: "facility_dining_hall", label: "Dining hall", type: "select", options: ["Yes", "No"] },
					{ name: "facility_common_room", label: "Common Room", type: "select", options: ["Yes", "No"] },
					{ name: "facility_computer_reading_room", label: "Computer cum reading Room", type: "select", options: ["Yes", "No"] },
					{ name: "facility_medical_room", label: "Medical Room", type: "select", options: ["Yes", "No"] },
					{ name: "facility_office_room", label: "Office Room", type: "select", options: ["Yes", "No"] },
					{ name: "facility_store_room", label: "Store Room", type: "select", options: ["Yes", "No"] },
					{ name: "facility_chowkidar_room", label: "Chowkidar Room", type: "select", options: ["Yes", "No"] },
					{ name: "facility_firefighting_arrangement", label: "Firefighting arrangement", type: "select", options: ["Yes", "No"] },
					{ name: "facility_ramps", label: "Ramps facility", type: "select", options: ["Yes", "No"] },
					{ name: "facility_disabled_friendly_toilets", label: "Disabled friendly toilets", type: "select", options: ["Yes", "No"] },
					{ name: "facility_warden", label: "Warden", type: "select", options: ["Yes", "No"] },
					{ name: "facility_boundary_wall", label: "Boundary wall", type: "select", options: ["Yes", "No"] },
					{ name: "facility_health_checkup", label: "Health checkup facility", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Maintenance of Records",
				fields: [],
				maintenanceTable: {
					records: [
						{ name: "record_cash_book", label: "Cash Book" },
						{ name: "record_ledger", label: "Ledger" },
						{ name: "record_student_admission_register", label: "Student admission register" },
						{ name: "record_attendance_students", label: "Attendance register of students" },
						{ name: "record_attendance_staff", label: "Attendance register of staff" },
						{ name: "record_stock_register", label: "Stock register" },
						{ name: "record_food_consumption", label: "Records of daily consumption of food articles" },
						{ name: "record_salary_payment", label: "Salary payment register" },
						{ name: "record_biodata_staff", label: "Biodata/personal files of staff" },
						{ name: "record_general_body_minutes", label: "Year wise records of minutes of general body meeting" },
					],
					extraFields: [
						{ name: "suggestions_from_staff", label: "Any suggestions from Staff", type: "textarea" },
						{ name: "suggestions_from_students", label: "Any suggestions from Students", type: "textarea" },
						{ name: "remarks_social_audit_team", label: "Remarks from Social Audit Team", type: "textarea" },
					],
				},
			},
		],
	},
	{
		matchName: "IRCA",
		description: "Integrated Rehabilitation Centre — patients, staff, treatment",
		colorHex: "#db2777",
		sections: [
			{
				title: "Centre basics",
				columns: 2,
				fields: [
					{ name: "irca_name", label: "Name of IRCA", type: "text", required: true, placeholder: "Centre name" },
					{ name: "is_operational", label: "Is IRCA operational?", type: "yesno" },
				],
			},
			{
				title: "Patients & staff",
				columns: 3,
				fields: [
					{ name: "seats_sanctioned", label: "Total sanctioned seats", type: "number" },
					{ name: "patients_records", label: "Patients as per records", type: "number" },
					{ name: "patients_found_audit", label: "Patients found (audit)", type: "number" },
					{ name: "patients_absent_total", label: "Total absent patients", type: "number" },
					{ name: "staff_records", label: "Staff as per records", type: "number" },
					{ name: "staff_verified", label: "Staff as per verification", type: "number" },
					{ name: "staff_qualification_matching", label: "Staff qualification matching", type: "number", placeholder: "Count" },
				],
			},
			{
				title: "Services",
				columns: 3,
				fields: [
					{ name: "doctor_available", label: "Doctor available?", type: "yesno" },
					{ name: "yoga_teacher_available", label: "Yoga teacher available?", type: "yesno" },
					{ name: "free_food_served", label: "Free food served?", type: "yesno" },
					{ name: "recreation_facility", label: "Recreation facility?", type: "yesno" },
					{ name: "treatment_fee_charged", label: "Fee charged for treatment?", type: "yesno" },
				],
			},
			{
				title: "Discharged patients",
				columns: 2,
				fields: [
					{ name: "discharged_verified", label: "Discharged patients verified", type: "number" },
					{ name: "discharged_satisfied", label: "Discharged patients satisfied", type: "number" },
				],
			},
			{
				title: "Finance & records",
				columns: 3,
				fields: [
					{ name: "fund_received", label: "Fund received?", type: "yesno" },
					{ name: "fund_pending_since", label: "If No, since when?", type: "text", placeholder: "Month/Year" },
					{ name: "using_pfms", label: "Using PFMS?", type: "yesno" },
					{ name: "mosje_display_board", label: "MoSJE display board?", type: "yesno" },
					{ name: "cashbook_available", label: "Cashbook available?", type: "yesno" },
					{ name: "cashbook_updated", label: "Cashbook updated?", type: "yesno" },
				],
			},
			{
				title: "Impact",
				columns: 1,
				fields: [
					{ name: "impact_remarks", label: "Impact of social audit", type: "textarea", placeholder: "Describe impact…" },
				],
			},
		],
	},
	{
		matchName: "IRCA under NAPDDR",
		description: "Full IRCA social audit format — organization, centre, patients, treatment, staff, records",
		colorHex: "#be123c",
		sections: [
			{
				title: "Basic Details",
				columns: 3,
				fields: [
					{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
					{ name: "district_id", label: "District Name", type: "district", required: true },
					{ name: "institute_id", label: "Institute", type: "institute", required: true, dependsOn: "district_id" },
					{ name: "social_audit_date_from", label: "Social Audit Date From", type: "date", required: true },
					{ name: "social_audit_date_to", label: "Social Audit Date To", type: "date", required: true },
					{ name: "financial_year", label: "Social Audit Period (FY)", type: "financial_year", required: true },
				],
			},
			{
				title: "Basic Information Related to Organization",
				columns: 4,
				fields: [
					{ name: "org_name", label: "Name of the organization", type: "text", required: true },
					{ name: "org_head_office_address", label: "Address of the Head Office", type: "text" },
					{ name: "org_contact_details", label: "Contact Details", type: "text" },
					{ name: "org_email", label: "E-mail address", type: "text" },
					{ name: "year_of_establishment", label: "Year of establishment", type: "select", options: yearOptions() },
					{ name: "registration_number", label: "Registration No.", type: "text" },
					{ name: "sources_of_funding", label: "Sources of funding", type: "text" },
					{ name: "grants_received_last_3fy_mosje", label: "Grant received in last 3 financial years from MoSJE", type: "select", options: ["Yes", "No"] },
					{ name: "contact_person_name", label: "Name of contact person (Secretary, director etc.)", type: "text" },
					{ name: "contact_person_phone", label: "Contact person phone number", type: "text" },
					{ name: "managing_body_composition", label: "Composition of Managing body", type: "select", options: ["Yes", "No"] },
					{ name: "state_level_coordinating_agency", label: "The organization is attached with which State Level Coordinating Agencies", type: "text" },
					{ name: "other_projects_details", label: "Details of any other projects being run by the organization", type: "text" },
				],
			},
			{
				title: "Basic Information Related to Centre",
				columns: 4,
				fields: [
					{ name: "centre_name", label: "Name of the Centre", type: "text", required: true },
					{ name: "centre_address", label: "Complete Address", type: "text" },
					{ name: "project_location", label: "Project Location", type: "select", options: ["Rural", "Urban"] },
					{ name: "centre_contact_numbers", label: "Contact Numbers", type: "text" },
					{ name: "centre_email", label: "E-mail Address", type: "text" },
					{ name: "centre_establishment_year", label: "Year of establishment of the Centre", type: "text" },
					{ name: "centre_grants_since_year", label: "Year from which centre has been receiving grants from MoSJE", type: "text" },
					{ name: "project_in_charge_name", label: "Name of the Project-in-charge", type: "text" },
					{ name: "last_grant_date", label: "Date of last grant received from the Ministry", type: "date" },
					{ name: "last_grant_amount", label: "Amount of last grant received from the Ministry", type: "number" },
					{ name: "centre_name_display_outside", label: "Whether the name of the Centre and the funding support from MoSJE are prominently displayed outside the Centre", type: "select", options: ["Yes", "No"] },
					{ name: "cctv_installed", label: "Whether the CCTV cameras are installed?", type: "select", options: ["Yes", "No"] },
					{ name: "has_own_website", label: "Does the organization have its own website?", type: "select", options: ["Yes", "No"] },
					{ name: "using_pfms", label: "Is PFMS being used for management of grants?", type: "select", options: ["Yes", "No"] },
					{ name: "building_provision", label: "Whether building is provided by District/State administration/ owned by the organization/ rent paid by the organization from its own resource", type: "select", options: ["District/State administration", "Owned by the organization", "Rent paid by the organization"] },
					{ name: "total_staff_as_per_record", label: "Total number of staff as per records", type: "number" },
					{ name: "total_staff_present_audit", label: "Total number of staff present during social audit", type: "number" },
				],
			},
			{
				title: "Services Related",
				columns: 4,
				fields: [
					{ name: "food_served_free", label: "Whether the food served is free of cost?", type: "select", options: ["Yes", "No"] },
					{ name: "food_menu_chart", label: "Does the centre have a food menu chart?", type: "select", options: ["Yes", "No"] },
					{ name: "allopathic_medicines_stock", label: "Whether allopathic medicines are available in stock as per the requirements of all the patients?", type: "select", options: ["Yes", "No"] },
					{ name: "slca_monitoring_visit", label: "Any monitoring visit/inspection done by the SLCA?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Details of Patients at Centre",
				columns: 4,
				fields: [
					{ name: "patients_admitted_last_fy", label: "Total number of patients admitted in centre in last one financial year", type: "number" },
					{ name: "treatment_fee_charged", label: "Whether any fee is charged for treatment?", type: "select", options: ["Yes", "No"] },
					{ name: "patients_waiting_treatment", label: "Number of patients waiting for treatment", type: "number" },
					{ name: "patients_relapsed_readmitted", label: "No. of persons who have relapsed and re-admitted to the same centre", type: "number" },
					{ name: "inpatients_upto_30_days", label: "In-patients admitted — Up to 30 days", type: "number" },
					{ name: "inpatients_31_to_60_days", label: "In-patients admitted — 31 to 60 days", type: "number" },
					{ name: "inpatients_more_than_60_days", label: "In-patients admitted — More than 60 days", type: "number" },
					{ name: "inpatients_total", label: "In-patients admitted — Total", type: "number", computeFrom: ["inpatients_upto_30_days", "inpatients_31_to_60_days", "inpatients_more_than_60_days"] },
				],
			},
			{
				title: "Detail of Follow Up Programmes/Activities Taken Up by the Centre",
				columns: 4,
				fields: [
					{ name: "counselling_to_discharged_patients", label: "No. of counselling provided to the discharged patients", type: "number" },
					{ name: "home_visits_undertaken", label: "Total number of home visits undertaken", type: "number" },
					{ name: "discharged_patients_visited", label: "No. of discharged patients visited", type: "number" },
					{ name: "outreach_programmes_camps", label: "Outreach programmes, camps, etc.", type: "number", placeholder: "No. of patients" },
					{ name: "rehabilitation_programmes", label: "Rehabilitation programmes given/referred by the centre", type: "number", placeholder: "No. of patients" },
					{ name: "self_help_groups_programmes", label: "Self Help groups programmes", type: "number", placeholder: "No. of patients" },
					{ name: "family_assistance_programme", label: "Family assistance programme", type: "number", placeholder: "No. of patients" },
					{ name: "half_way_home", label: "Half-way Home", type: "number", placeholder: "No. of patients" },
					{ name: "drop_in_centres", label: "Drop in centres", type: "number", placeholder: "No. of patients" },
					{ name: "vocational_training_services", label: "Vocational training services", type: "number", placeholder: "No. of patients" },
					{ name: "work_place_support", label: "Work place support", type: "select", options: ["Yes", "No"] },
					{ name: "educational_support", label: "Educational support", type: "select", options: ["Yes", "No"] },
					{ name: "any_other_services", label: "Any other services", type: "text" },
				],
			},
			{
				title: "Availability of Records/ Documents",
				columns: 4,
				fields: [
					{ name: "record_patients_details_register", label: "Patients details register", type: "select", options: ["Yes", "No"] },
					{ name: "record_admission_discharge_register", label: "Admission/Discharge Register", type: "select", options: ["Yes", "No"] },
					{ name: "record_clients_relapsed", label: "Record of clients who relapsed", type: "select", options: ["Yes", "No"] },
					{ name: "record_counselling_therapy", label: "Counselling & Therapy record", type: "select", options: ["Yes", "No"] },
					{ name: "record_endorsement_letter_free_treatment", label: "Letter of endorsement for free treatment services", type: "select", options: ["Yes", "No"] },
					{ name: "record_declaration_indemnity_form", label: "Declaration cum indemnity form (for all patients)", type: "select", options: ["Yes", "No"] },
					{ name: "record_attendance_register", label: "Attendance Register (for both the staff and patients in the Centre)", type: "select", options: ["Yes", "No"] },
					{ name: "record_salary_register", label: "Salary Register", type: "select", options: ["Yes", "No"] },
					{ name: "record_cashbook", label: "Cashbook", type: "select", options: ["Yes", "No"] },
					{ name: "record_ledger_book", label: "Ledger book", type: "select", options: ["Yes", "No"] },
					{ name: "record_assets_register", label: "Register of assets (Details of physical assets, date of procurement, replacement etc.)", type: "select", options: ["Yes", "No"] },
					{ name: "record_medicines_stock_register", label: "Medicines Stock Register (Including details of medicine procured, issued, balance etc.)", type: "select", options: ["Yes", "No"] },
					{ name: "record_bills_vouchers", label: "Bills and Vouchers for all expenditure for audit period", type: "select", options: ["Yes", "No"] },
					{ name: "record_counselling_register", label: "Register of counselling to patient", type: "select", options: ["Yes", "No"] },
					{ name: "record_doctor_visit_register", label: "Doctor Visit Register", type: "select", options: ["Yes", "No"] },
					{ name: "record_staff_establishment_records", label: "Establishment records of the Staff", type: "select", options: ["Yes", "No"] },
					{ name: "record_clinical_establishment_record", label: "Clinical establishment record/State", type: "select", options: ["Yes", "No"] },
					{ name: "record_awareness_programme_register", label: "Awareness programme register", type: "select", options: ["Yes", "No"] },
					{ name: "record_patient_assessment_forms", label: "Assessment forms of the patients", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Staffing Pattern",
				fields: [],
				repeating: {
					arrayName: "staffing_pattern",
					rowFields: [
						{ name: "position", label: "Position", type: "text" },
						{ name: "person_name", label: "Name of person", type: "text" },
						{ name: "educational_qualification", label: "Educational Qualification", type: "text" },
						{ name: "date_of_joining", label: "Date of Joining the Centre", type: "date" },
						{ name: "present_or_absent", label: "Whether present/absent during social audit", type: "select", options: ["Present", "Absent"] },
						{ name: "reasons_for_absence", label: "Reasons for absence", type: "text" },
					],
				},
			},
			{
				title: "Information Related To Organization/Centre",
				layout: "paired",
				fields: [
					{ name: "referral_linkages_other_orgs", label: "Are there referral and linkages to other organizations?", type: "select", options: ["Yes", "No"] },
					{ name: "referral_linkages_other_orgs_remark", label: "Remark", type: "text" },
					{ name: "adequate_staff", label: "Does the centre have adequate number of staff?", type: "select", options: ["Yes", "No"] },
					{ name: "adequate_staff_remark", label: "Remark", type: "text" },
					{ name: "location_accessibility", label: "Location and accessibility of the Centre", type: "text" },
					{ name: "location_accessibility_remark", label: "Remark", type: "text" },
				],
			},
			{
				title: "Transparency",
				layout: "table",
				fields: [
					{ name: "public_display_board", label: "Whether there is a public display board indicating the name & type of the institution?", type: "yesno" },
					{ name: "public_board_mosje_support", label: "Whether there is any public board indicating that the facility is run with support from MSJE?", type: "yesno" },
					{ name: "entitlements_publicly_displayed", label: "Whether the Entitlements of Residents is publicly displayed anywhere in the home?", type: "yesno" },
					{ name: "citizen_charter_displayed", label: "Is the citizen charter publicly displayed in the center?", type: "yesno" },
					{ name: "patients_rights_displayed", label: "Are patient's rights displayed in the center?", type: "yesno" },
					{ name: "info_pamphlets_available", label: "Whether information pamphlets, handouts, posters, charts etc. are available for public and clients", type: "yesno" },
				],
			},
			{
				title: "Infrastructure",
				layout: "table",
				fields: [
					{ name: "built_area_15_bedded", label: "Built in area of the centre for 15 bedded (metro area: 2000 sq ft, small towns/villages: 3000 sq ft)", type: "number" },
					{ name: "built_area_30_bedded", label: "Built in area of the centre for 30 bedded (metro area: 3000 sq ft, small towns/villages: 4000 sq ft)", type: "number" },
					{ name: "beds_sanctioned_last_fy", label: "No. of beds sanctioned in last financial year", type: "number" },
					{ name: "beds_found_during_audit", label: "No. of beds found during social audit", type: "number" },
					{ name: "lighting_arrangements_functional", label: "Whether Lighting arrangements, fans etc. available and functioning", type: "yesno" },
					{ name: "ventilated_rooms_spacing", label: "Does the home have properly ventilated rooms with sufficient space between the beds of the beneficiaries for their easy movement?", type: "yesno" },
					{ name: "bathrooms_toilets_clean", label: "Are the bathrooms and toilets clean?", type: "yesno" },
					{ name: "reception_enquiry_counter", label: "Is there a reception, enquiry and registration counter?", type: "yesno" },
					{ name: "cubical_for_consultation", label: "Is there a cubical/rooms for consultation for physical examination?", type: "yesno" },
					{ name: "nursing_station_available", label: "Is there a nursing station with facilities to store drugs, linen and records of patients?", type: "yesno" },
					{ name: "privacy_counselling_facilities", label: "Are there facilities with privacy for providing individual counselling, group therapy, re-educative sessions and family counselling?", type: "yesno" },
					{ name: "facilities_airy_comfortable", label: "Are these facilities airy with comfortable sitting with chairs, blackboard, chalks and other materials?", type: "yesno" },
					{ name: "recreation_facilities_available", label: "Are recreation facilities available at centre (i.e. TV, Library, Indoor Games-carrom, chess and outdoor games etc)?", type: "yesno" },
					{ name: "storage_personal_belongings", label: "Are the patients provided storage space to store personal belongings?", type: "yesno" },
					{ name: "waiting_space_available", label: "Is waiting space with sitting arrangements available?", type: "yesno" },
				],
			},
			{
				title: "Accountability",
				layout: "table",
				fields: [
					{ name: "complaint_box_available", label: "Is there a complaint box in the centre?", type: "yesno" },
					{ name: "complaint_register_available", label: "Is complaint register available?", type: "yesno" },
				],
			},
			{
				title: "Maintenance of Records",
				layout: "table",
				fields: [
					{ name: "cashbook_maintained_well", label: "Is the cash book maintained well?", type: "yesno" },
					{ name: "ledger_maintained_well", label: "Is the ledger maintained well?", type: "yesno" },
					{ name: "stock_register_maintained_well", label: "Is the stock register maintained well?", type: "yesno" },
					{ name: "consumables_register_maintained_well", label: "Is the 'Register for consumable items' maintained well?", type: "yesno" },
					{ name: "attendance_register_maintained_well", label: "Is the 'Attendance Register' for both staff and residents maintained well?", type: "yesno" },
					{ name: "assets_register_maintained_well", label: "Is the register of assets maintained well?", type: "yesno" },
					{ name: "governing_body_minutes_available", label: "Is the year-wise record of the 'Governing body Meeting Minutes' available?", type: "yesno" },
					{ name: "awareness_programme_register_available", label: "Is the awareness programme register available?", type: "yesno" },
					{ name: "out_patient_register_available", label: "Is the out-patient register available? If yes, is it well maintained?", type: "yesno" },
					{ name: "admission_register_maintained_well", label: "Is the admission register maintained well?", type: "yesno" },
					{ name: "clients_relapsed_readmission_record", label: "Record of clients who relapsed – whether they are re-admitted to the same centre or referred to another facility?", type: "text" },
				],
			},
			{
				title: "Staff — Focus Group Discussion",
				layout: "paired",
				fields: [
					{ name: "fgd_missing_improvement", label: "What is missing or needs improvement?", type: "textarea" },
					{ name: "fgd_missing_improvement_remark", label: "Remark", type: "text" },
					{ name: "fgd_knowledge_local_services", label: "Knowledge of its services in the proximate locality", type: "textarea" },
					{ name: "fgd_knowledge_local_services_remark", label: "Remark", type: "text" },
				],
			},
			{
				title: "Training",
				columns: 3,
				fields: [
					{ name: "training_course_name", label: "Name of training course", type: "text" },
					{ name: "training_period_dates", label: "Period of training with Dates", type: "date" },
					{ name: "training_provider", label: "Training provider", type: "select", options: ["SLCA", "NCDAP", "Other"] },
				],
			},
			{
				title: "Availability of Records/ Documents — Patient Records",
				layout: "paired",
				fields: [
					{ name: "unique_registration_number_file", label: "Is a file with a unique registration number created for each new patient?", type: "select", options: ["Yes", "No"] },
					{ name: "unique_registration_number_file_remark", label: "Remark", type: "text" },
					{ name: "treatment_plan_prepared_on_file", label: "Is a treatment/intervention plan prepared for each patient and is it on file?", type: "select", options: ["Yes", "No"] },
					{ name: "treatment_plan_prepared_on_file_remark", label: "Remark", type: "text" },
					{ name: "medication_dispensation_records_maintained", label: "Are medication dispensation records maintained well?", type: "select", options: ["Yes", "No"] },
					{ name: "medication_dispensation_records_maintained_remark", label: "Remark", type: "text" },
					{ name: "centre_used_adequately", label: "Is the Centre being used adequately?", type: "select", options: ["Yes", "No"] },
					{ name: "centre_used_adequately_remark", label: "Remark", type: "text" },
				],
			},
			{
				title: "Services Related — Clinical",
				layout: "paired",
				fields: [
					{ name: "clinical_assessment_by_doctor", label: "Is clinical assessment (history taking & examination) done by a trained and competent doctor?", type: "select", options: ["Yes", "No"] },
					{ name: "clinical_assessment_by_doctor_remark", label: "Remark", type: "text" },
					{ name: "counselling_by_trained_professional", label: "Do patients receive counselling by trained social worker/counsellor/psychologist?", type: "select", options: ["Yes", "No"] },
					{ name: "counselling_by_trained_professional_remark", label: "Remark", type: "text" },
					{ name: "medicines_free_of_cost", label: "Are allopathic medicines provided free of cost to the patients?", type: "select", options: ["Yes", "No"] },
					{ name: "medicines_free_of_cost_remark", label: "Remark", type: "text" },
					{ name: "counselling_appropriate_systematic", label: "Whether Counselling services are being provided appropriately and systematically", type: "select", options: ["Yes", "No"] },
					{ name: "counselling_appropriate_systematic_remark", label: "Remark", type: "text" },
				],
			},
			{
				title: "Quality of Services",
				columns: 2,
				fields: [
					{ name: "quality_of_food", label: "Quality of food", type: "select", options: ["Very Good", "Good", "Ok", "Poor", "Very Poor"] },
					{ name: "quality_of_food_remark", label: "Remark", type: "text" },
					{ name: "quantity_of_food", label: "Quantity of food", type: "select", options: ["Sufficient", "Not sufficient"] },
					{ name: "quantity_of_food_remark", label: "Remark", type: "text" },
					{ name: "cleanliness_of_institution", label: "Cleanliness of the institution", type: "select", options: ["Very Good", "Good", "Ok", "Poor", "Very Poor"] },
					{ name: "cleanliness_of_institution_remark", label: "Remark", type: "text" },
					{ name: "physical_verbal_violence_case", label: "Any specific case of physical / verbal violence?", type: "select", options: ["Yes", "No"] },
					{ name: "physical_verbal_violence_case_remark", label: "Remark", type: "text" },
				],
			},
			{
				title: "Entitlements of Residents",
				layout: "table",
				fields: [
					{ name: "meals_served_all_times", label: "Are morning tea, breakfast, lunch, evening tea and dinner served to the residents?", type: "yesno" },
					{ name: "good_quality_food_sufficient", label: "Is good quality food provided in sufficient quantity (at least 1700 calories and 50 gms of protein) everyday?", type: "yesno" },
					{ name: "reading_material_access", label: "Does the residents have access to books, 3-4 Magazines, 2-3 newspapers (in regional/local language)", type: "yesno" },
				],
			},
		],
	},
	{
		matchName: "Senior Citizen Home",
		description: "Beds, beneficiaries, staff, food, recreation, PFMS",
		colorHex: "#7c3aed",
		sections: [
			{
				title: "Home basics",
				columns: 2,
				fields: [
					{ name: "home_name", label: "Name of Senior Citizen Home", type: "text", required: true, placeholder: "Home name" },
					{ name: "is_operational", label: "Is home operational?", type: "yesno" },
					{ name: "bed_capacity_sanctioned", label: "Total sanctioned bed capacity", type: "number" },
				],
			},
			{
				title: "Beneficiaries & staff",
				columns: 3,
				fields: [
					{ name: "beneficiaries_records", label: "Beneficiaries as per records", type: "number" },
					{ name: "beneficiaries_found_audit", label: "Beneficiaries found (audit)", type: "number" },
					{ name: "beneficiaries_absent", label: "Absent beneficiaries", type: "number" },
					{ name: "staff_records", label: "Staff as per records", type: "number" },
					{ name: "staff_verified", label: "Staff as per verification", type: "number" },
					{ name: "beneficiaries_old_age_pension", label: "Beneficiaries getting old-age pension", type: "number" },
				],
			},
			{
				title: "Services & infrastructure",
				columns: 3,
				fields: [
					{ name: "doctor_available", label: "Doctor available?", type: "yesno" },
					{ name: "yoga_teacher_available", label: "Yoga teacher?", type: "yesno" },
					{ name: "recreation_facility", label: "Recreation facility?", type: "yesno" },
					{ name: "nutritional_food_served", label: "Nutritional food served?", type: "yesno" },
					{ name: "room_type", label: "Separate rooms / dormitory?", type: "select", options: ["Separate rooms", "Dormitory", "Both"] },
				],
			},
			{
				title: "Finance & records",
				columns: 3,
				fields: [
					{ name: "fund_received", label: "Fund received?", type: "yesno" },
					{ name: "fund_pending_since", label: "If No, since when?", type: "text", placeholder: "Month/Year" },
					{ name: "using_pfms", label: "Using PFMS?", type: "yesno" },
					{ name: "mosje_display_board", label: "MoSJE display board?", type: "yesno" },
					{ name: "cashbook_available", label: "Cashbook available?", type: "yesno" },
					{ name: "cashbook_updated", label: "Cashbook updated?", type: "yesno" },
				],
			},
			{
				title: "Impact",
				columns: 1,
				fields: [
					{ name: "impact_remarks", label: "Impact of social audit", type: "textarea", placeholder: "Describe impact…" },
				],
			},
		],
	},
	{
		matchName: "Sr. Citizen Home under AVYAY",
		description: "Full Senior Citizen Home social audit format — organization, centre, services, records, staff, residents",
		colorHex: "#6d28d9",
		sections: [
			{
				title: "Basic Details",
				columns: 3,
				fields: [
					{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
					{ name: "district_id", label: "District", type: "district", required: true },
					{ name: "institute_id", label: "Institute", type: "institute", required: true, dependsOn: "district_id" },
					{ name: "social_audit_date_from", label: "Social Audit Date From", type: "date", required: true },
					{ name: "social_audit_date_to", label: "Social Audit Date To", type: "date", required: true },
					{ name: "financial_year", label: "Financial year", type: "financial_year", required: true },
				],
			},
			{
				title: "Basic Information Related to Organization",
				columns: 3,
				fields: [
					{ name: "org_name", label: "Name of the organization", type: "text", required: true },
					{ name: "org_head_office_address", label: "Address of the head office", type: "text" },
					{ name: "org_email", label: "E-mail address", type: "text" },
					{ name: "year_of_establishment", label: "Year of establishment", type: "select", options: yearOptions() },
					{ name: "registration_number", label: "Registration Number", type: "text" },
					{ name: "sources_of_funding", label: "Sources of funding", type: "text" },
					{ name: "grant_in_aid_commencement_year", label: "Year of commencement of grant-in-aid", type: "text" },
					{ name: "grants_received_last_3fy_mosje", label: "Grants received in last three FY from MoSJE", type: "select", options: ["Yes", "No"] },
					{ name: "contact_person_name", label: "Name of contact person (Secretary, director etc.)", type: "text" },
					{ name: "contact_person_phone", label: "Contact person phone number", type: "text" },
					{ name: "managing_body_composition", label: "Composition of Managing body", type: "select", options: ["Yes", "No"] },
					{ name: "managing_committee_as_per_proforma", label: "Whether composition of managing committee is indicated as per prescribed proforma", type: "text" },
					{ name: "other_projects_details", label: "Details of any other projects being run by the organization", type: "text" },
					{ name: "project_wise_accounts_maintained", label: "Whether separate project-wise accounts have been maintained for grants sanctioned?", type: "text" },
				],
			},
			{
				title: "Centre Related",
				columns: 3,
				fields: [
					{ name: "nature_of_program", label: "Nature of Program", type: "text" },
					{ name: "project_location_address", label: "Complete address of location where project is being implemented", type: "text" },
					{ name: "project_recognized_state_govt", label: "Whether the project is recognized by the State Government", type: "text" },
					{ name: "project_location", label: "Project Location", type: "select", options: ["Rural", "Urban"] },
					{ name: "centre_establishment_year", label: "Year of establishment of the Centre", type: "text" },
					{ name: "centre_grants_since_year", label: "Year from which centre has been receiving grants from MoSJE", type: "text" },
					{ name: "project_in_charge_name", label: "Name of the Project-in-charge", type: "text" },
					{ name: "last_grant_date", label: "Date of last grant received from the Ministry", type: "date" },
					{ name: "last_grant_amount", label: "Amount of last grant received from the Ministry", type: "number" },
					{ name: "nearest_similar_institution_name", label: "Name of nearest Government Institution/NGO providing similar facilities", type: "text" },
					{ name: "nearest_similar_institution_address", label: "Locational address of nearest Government Institution/NGO", type: "text" },
					{ name: "building_rented_or_owned", label: "Whether building is Rented or Owned", type: "select", options: ["Rented", "Owned"] },
				],
			},
			{
				title: "Services Related",
				columns: 4,
				fields: [
					{ name: "nutritional_food_served_details", label: "Whether nutritional food is being served to the beneficiaries", type: "text" },
					{ name: "medical_checkup_facilities", label: "Facilities available for medical checkup and treatment of the aged", type: "text" },
					{ name: "entertainment_facilities", label: "Are entertainment facilities available at Centre for the beneficiaries", type: "text" },
					{ name: "vocational_training_imparted", label: "Are vocational training imparted to the beneficiaries", type: "text" },
					{ name: "other_service_rendered", label: "Any other service rendered at the Centre for the beneficiaries", type: "text" },
					{ name: "productive_activity_facilities", label: "Are any facilities provided for productive activity for the beneficiaries", type: "text" },
					{ name: "social_service_undertaken", label: "Whether any social service is undertaken by the Centre", type: "select", options: ["Teaching", "Vocational training", "Crèche service", "community service"] },
					{ name: "linkages_other_org", label: "Linkages with any other organization/institution for providing Nutrition, Recreation, Health, Vocational Training", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Fees related",
				columns: 3,
				fields: [
					{ name: "user_fee_charged", label: "Whether the centre is charging user fee/fees from the beneficiaries?", type: "select", options: ["Yes", "No"] },
					{ name: "user_fee_monthly", label: "Monthly charges", type: "number" },
					{ name: "user_fee_annual", label: "Annual charges", type: "number" },
					{ name: "user_charges_reflected_accounts", label: "Whether user charges collected are properly reflected in the NGO accounts?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Transparency Related",
				columns: 3,
				fields: [
					{ name: "public_name_board", label: "Public name board indicating the name & type of the institution?", type: "select", options: ["Yes", "No"] },
					{ name: "public_board_mosje_support", label: "Public board indicating that the facility is run with support from MoSJE?", type: "select", options: ["Yes", "No"] },
					{ name: "admission_list_publicly_displayed", label: "Whether the list of people who can be admitted is publicly displayed?", type: "select", options: ["Yes", "No"] },
					{ name: "entitlements_publicly_displayed", label: "Whether the entitlements of residents are publicly displayed anywhere in the home?", type: "select", options: ["Yes", "No"] },
					{ name: "cctv_installed_functional", label: "Are the CCTV cameras installed and functional at Centre?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Accountability",
				columns: 2,
				fields: [
					{ name: "complaint_box_available", label: "Is there a complaint box in the home?", type: "select", options: ["Yes", "No"] },
					{ name: "complaint_register_available", label: "Is there a complaint register where complaints are recorded", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Maintenance of Records",
				columns: 4,
				fields: [
					{ name: "record_cashbook_maintained", label: "Cash book — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_cashbook_updated", label: "Cash book — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_ledger_maintained", label: "Ledger — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_ledger_updated", label: "Ledger — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_assets_register_maintained", label: "Register of Assets — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_assets_register_updated", label: "Register of Assets — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_consumables_register_maintained", label: "Register for consumable items — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_consumables_register_updated", label: "Register for consumable items — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_staff_attendance_maintained", label: "Attendance Register (staff) — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_staff_attendance_updated", label: "Attendance Register (staff) — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_residents_movement_maintained", label: "Movement Register (residents) — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_residents_movement_updated", label: "Movement Register (residents) — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_governing_body_minutes_maintained", label: "Governing Body Meeting Minutes — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_governing_body_minutes_updated", label: "Governing Body Meeting Minutes — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_resident_kin_assets_maintained", label: "Records of resident's kin/assets/disposal — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_resident_kin_assets_updated", label: "Records of resident's kin/assets/disposal — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_staff_salary_maintained", label: "Staff salary register — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_staff_salary_updated", label: "Staff salary register — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_medicine_stock_maintained", label: "Medicine stock register — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_medicine_stock_updated", label: "Medicine stock register — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_food_nutrition_maintained", label: "Food/Nutrition Register — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_food_nutrition_updated", label: "Food/Nutrition Register — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_admission_discharge_maintained", label: "Admission/Discharge/History register — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_admission_discharge_updated", label: "Admission/Discharge/History register — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_donation_register_maintained", label: "Donation Register — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_donation_register_updated", label: "Donation Register — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_beneficiary_case_history_maintained", label: "Beneficiary case history file — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_beneficiary_case_history_updated", label: "Beneficiary case history file — Updated", type: "select", options: ["Yes", "No"] },
					{ name: "record_death_dropout_maintained", label: "Death/drop out register — Maintained", type: "select", options: ["Yes", "No"] },
					{ name: "record_death_dropout_updated", label: "Death/drop out register — Updated", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Staffs Related",
				columns: 3,
				fields: [
					{ name: "total_staff_as_per_record", label: "Total number of staffs as per record", type: "number" },
					{ name: "staff_details", label: "Names of the staffs/designation/educational qualification/work experience/any training undergone/working since/salary withdrawn", type: "textarea" },
					{ name: "superintendent_resides_at_centre", label: "Does the superintendent reside at the centre?", type: "select", options: ["Yes", "No"] },
					{ name: "doctor_visits_twice_week", label: "Is there a doctor (MBBS/BAMS/BHMS) who visits the center at least twice a week?", type: "select", options: ["Yes", "No"] },
					{ name: "doctor_visits_twice_week_details", label: "Details", type: "text" },
					{ name: "doctor_visits_emergency", label: "Does the doctor visit the centre in case of any emergency?", type: "select", options: ["Yes", "No"] },
					{ name: "doctor_visits_emergency_details", label: "Details", type: "text" },
					{ name: "doctor_stays_close", label: "Does the doctor stay close to the centre?", type: "select", options: ["Yes", "No"] },
					{ name: "full_time_doctor", label: "Is there a full-time doctor for continuous care home?", type: "select", options: ["Yes", "No"] },
					{ name: "yoga_therapist_visits", label: "Is there a Yoga therapist who visits at least three times a week for a minimum one hour per day?", type: "select", options: ["Yes", "No"] },
					{ name: "yoga_therapist_visits_details", label: "Details", type: "text" },
					{ name: "social_worker_visits_daily", label: "Does a social worker visit the Centre at least 2 hours every day?", type: "select", options: ["Yes", "No"] },
					{ name: "social_worker_visits_daily_details", label: "Details", type: "text" },
					{ name: "nurse_visits_daily", label: "Does a Nurse visit the centre for a minimum of 2 hours per day, 5 days a week, for basic medical checkup/nursing/geriatric care?", type: "select", options: ["Yes", "No"] },
					{ name: "nurse_visits_daily_details", label: "Details", type: "text" },
					{ name: "resident_cooks_adequate", label: "Is there 1 or 2 resident cooks residing in the center? (1 for 25 beneficiaries, 2 for 50)", type: "select", options: ["Yes", "No"] },
					{ name: "resident_cooks_adequate_details", label: "Details", type: "text" },
					{ name: "multi_tasking_staff_sufficient", label: "Are there sufficient Multi-tasking staffs (chowkidar, helper, cleaner) residing at the Centre?", type: "select", options: ["Yes", "No"] },
					{ name: "multi_tasking_staff_sufficient_details", label: "Details", type: "text" },
				],
			},
			{
				title: "Beneficiaries/Residents Related",
				columns: 3,
				fields: [
					{ name: "total_beneficiaries_as_per_record", label: "Total number of beneficiaries/residents as per record", type: "number" },
					{ name: "beneficiaries_present_names", label: "Names of beneficiaries found present during verification", type: "textarea" },
					{ name: "beneficiaries_family_financial_support", label: "Total beneficiaries/residents receiving financial support from family/relatives/anyone", type: "number" },
					{ name: "beneficiaries_old_age_assistance", label: "Total beneficiaries/residents availing old age assistance from central/state government", type: "number" },
					{ name: "beneficiaries_other_medical_issues", label: "Total beneficiaries/residents suffering from Medical issues apart from age related health issue", type: "number" },
					{ name: "beneficiaries_with_aadhaar", label: "Total beneficiaries/residents who have Aadhaar card", type: "number" },
					{ name: "beneficiaries_with_nfsa_card", label: "Total beneficiaries/residents who have NFSA card (ration card)", type: "number" },
					{ name: "beneficiaries_need_assisted_devices", label: "Total beneficiaries/residents who need assisted living devices", type: "number" },
					{ name: "beneficiaries_satisfied_with_supplies", label: "Whether the beneficiaries/residents are satisfied with the supplies (Medicines, clothes, food, daily essentials, water)", type: "select", options: ["Yes", "No"] },
					{ name: "staff_behavior_attitude", label: "Behavior and attitude of the staffs towards the beneficiaries/residents", type: "text" },
				],
			},
			{
				title: "Entitlements of Residents",
				columns: 3,
				fields: [
					{ name: "meals_served_all_times", label: "Are morning tea, breakfast, lunch, evening tea and dinner served to the residents?", type: "select", options: ["Yes", "No"] },
					{ name: "good_quality_food_sufficient", label: "Is good quality food provided in sufficient quantity?", type: "select", options: ["Yes", "No"] },
					{ name: "reading_material_access", label: "Do the residents have access to books, magazines, newspapers in regional/local languages?", type: "select", options: ["Yes", "No"] },
					{ name: "outings_twice_a_month", label: "Are the residents taken for two outings in a month?", type: "select", options: ["Yes", "No"] },
					{ name: "computer_with_internet", label: "Does the home have a computer with an internet connection?", type: "select", options: ["Yes", "No"] },
					{ name: "tv_with_cable", label: "Does the home have a TV with cable connection?", type: "select", options: ["Yes", "No"] },
					{ name: "separate_reading_room", label: "Is there a separate reading room?", type: "select", options: ["Yes", "No"] },
					{ name: "four_pairs_clothing_per_year", label: "Are the residents being provided with 4 pairs of clothing in a year?", type: "select", options: ["Yes", "No"] },
					{ name: "belongings_storage_provision", label: "Is there any provision for storage of the belongings of the beneficiaries?", type: "select", options: ["Yes", "No"] },
					{ name: "funeral_expenses_provided", label: "Are funeral expenses up to Rs 10,000 provided by the home?", type: "select", options: ["Yes", "No"] },
					{ name: "registered_ayushman_or_state_scheme", label: "Are the residents registered under Ayushman Bharat or other state scheme?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Health Related",
				columns: 4,
				fields: [
					{ name: "separate_medical_room", label: "Is there a separate medical room available at centre?", type: "select", options: ["Yes", "No"] },
					{ name: "first_aid_kit_available", label: "Is there a first aid kit available in the home?", type: "select", options: ["Yes", "No"] },
					{ name: "glucometer_available", label: "Is there a glucometer available in the home?", type: "select", options: ["Yes", "No"] },
					{ name: "bp_monitoring_machine_available", label: "Is there a BP monitoring machine available in the home?", type: "select", options: ["Yes", "No"] },
					{ name: "weighing_machine_available", label: "Is there a weighing machine available in the home?", type: "select", options: ["Yes", "No"] },
					{ name: "prescribed_medicines_available", label: "Are medicines, as prescribed by doctor, available for the residents?", type: "select", options: ["Yes", "No"] },
					{ name: "regular_health_camps", label: "Are regular health camps conducted?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Infrastructure Related",
				columns: 4,
				fields: [
					{ name: "building_space_adequate", label: "Is the building space adequate enough to run the project?", type: "select", options: ["Yes", "No"] },
					{ name: "total_rooms", label: "Total number of rooms", type: "number" },
					{ name: "separate_rooms_men_women", label: "Are there separate rooms for men and women?", type: "select", options: ["Yes", "No"] },
					{ name: "separate_recreation_room", label: "Is there separate recreation room available?", type: "select", options: ["Yes", "No"] },
					{ name: "separate_kitchen_room", label: "Is a separate room for kitchen available at centre?", type: "select", options: ["Yes", "No"] },
					{ name: "dining_hall_available", label: "Is there a dining hall available for residents?", type: "select", options: ["Yes", "No"] },
					{ name: "rooms_veranda_kitchen_clean", label: "Whether the rooms, veranda/courtyard and kitchen are neat and cleaned?", type: "select", options: ["Yes", "No"] },
					{ name: "bathrooms_toilets_clean", label: "Whether the bathrooms and toilets are neat and cleaned?", type: "select", options: ["Yes", "No"] },
					{ name: "rooms_properly_ventilated", label: "Does the home have properly ventilated rooms with sufficient space between beds?", type: "select", options: ["Yes", "No"] },
					{ name: "floors_anti_slippery", label: "Are the floors in the home anti-slippery?", type: "select", options: ["Yes", "No"] },
					{ name: "separate_toilets_male_female", label: "Are there separate toilets for males and females?", type: "select", options: ["Yes", "No"] },
					{ name: "toilets_antislip_railing", label: "Do the bathrooms and toilets have anti-slippery tiles and railing for hand support?", type: "select", options: ["Yes", "No"] },
					{ name: "ramp_facilities_railings", label: "Does the home have ramp facilities and railings for easy access?", type: "select", options: ["Yes", "No"] },
					{ name: "fixtures_working_condition", label: "Whether the fixtures/fitments e.g. lights, fans, taps are in working condition", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Issues and Findings",
				columns: 2,
				fields: [
					{ name: "attendance_beneficiaries_present", label: "Attendance related — Beneficiaries present during verification", type: "number" },
					{ name: "attendance_beneficiaries_absent", label: "Attendance related — Beneficiaries absent during verification", type: "number" },
					{ name: "attendance_staff_present", label: "Attendance related — Staffs present during verification", type: "number" },
					{ name: "attendance_staff_absent", label: "Attendance related — Staffs absent during verification", type: "number" },
					{ name: "issue_infrastructure_related", label: "Infrastructure related", type: "textarea" },
					{ name: "issue_hr_related", label: "HR related", type: "textarea" },
					{ name: "issue_services_supplies_related", label: "Services and supplies related", type: "textarea" },
					{ name: "issue_behavior_related", label: "Behavior related", type: "textarea" },
					{ name: "issue_activities_recreation_related", label: "Activities/Recreation related", type: "textarea" },
					{ name: "issue_documents_related", label: "Documents related", type: "textarea" },
					{ name: "issue_institution_functioning", label: "Institution/society's functioning", type: "textarea" },
					{ name: "issue_grievance_related", label: "Grievance related", type: "textarea" },
					{ name: "issue_any_other", label: "Any other issues", type: "textarea" },
				],
			},
		],
	},
	{
		matchName: "Residential Schools — SHRESHTA",
		description: "CBSE/NGO schools — students, staff, CCTV, PFMS",
		colorHex: "#0891b2",
		sections: [
			{
				title: "School identification",
				columns: 3,
				fields: [
					{ name: "school_name", label: "Name of school", type: "text", required: true, placeholder: "School name" },
					{ name: "school_type", label: "Type of school", type: "select", options: ["CBSE", "NGO"] },
					{ name: "is_operational", label: "Is school operational?", type: "yesno" },
				],
			},
			{
				title: "Students & staff",
				columns: 3,
				fields: [
					{ name: "seats_sanctioned", label: "Total seats sanctioned", type: "number" },
					{ name: "students_records", label: "Students as per records", type: "number" },
					{ name: "students_verified", label: "Students as per verification", type: "number" },
					{ name: "staff_sanctioned", label: "Total staff sanctioned", type: "number" },
					{ name: "staff_found_audit", label: "Staff found during audit", type: "number" },
					{ name: "staff_qualification_matching", label: "Staff qualification matching", type: "number", placeholder: "Count" },
				],
			},
			{
				title: "Infrastructure & finance",
				columns: 3,
				fields: [
					{ name: "hostel_facility", label: "Hostel facility?", type: "yesno" },
					{ name: "biometric_attendance", label: "Biometric attendance?", type: "yesno" },
					{ name: "cctv_camera", label: "CCTV camera?", type: "yesno" },
					{ name: "cctv_functional", label: "CCTV functional?", type: "yesno" },
					{ name: "fund_received", label: "Fund received from MoSJE?", type: "yesno" },
					{ name: "using_pfms", label: "Using PFMS?", type: "yesno" },
					{ name: "mosje_display_board", label: "MoSJE display board?", type: "yesno" },
					{ name: "cashbook_available", label: "Cashbook available?", type: "yesno" },
					{ name: "cashbook_updated", label: "Cashbook updated?", type: "yesno" },
				],
			},
			{
				title: "Impact",
				columns: 1,
				fields: [
					{ name: "impact_remarks", label: "Impact of social audit", type: "textarea", placeholder: "Describe impact…" },
				],
			},
		],
	},
	{
		matchName: "Schools under SHRESHTA Mode 2",
		description: "Social Audit Format for Verification of Residential School (for Mode-II School)",
		colorHex: "#b45309",
		sections: [
			{
				title: "General Information",
				columns: 4,
				fields: [
					{ name: "financial_year", label: "Financial year", type: "financial_year", required: true },
					{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
					{ name: "district_id", label: "District", type: "district", required: true },
					{ name: "institute_id", label: "Institute", type: "institute", required: true, dependsOn: "district_id" },
					{ name: "project_name", label: "Name of the Project", type: "text" },
					{ name: "org_name", label: "Name of the Organization", type: "text", required: true },
					{ name: "project_location_address", label: "Location of Project — full address", type: "text" },
					{ name: "project_location_phone", label: "Location of Project — Phone Number", type: "text" },
					{ name: "org_affiliated_board", label: "Organization is affiliated with state or central board", type: "select", options: ["State", "Central Board"] },
					{ name: "project_specific_id", label: "Project Specific ID", type: "text" },
					{ name: "president_secretary_name", label: "Full name of President and Secretary of the organization", type: "text" },
					{ name: "org_has_own_website", label: "Whether the organization has its own website?", type: "select", options: ["Yes", "No"] },
					{ name: "website_has_all_info", label: "Does the website contain all the information about organization, staffs, students and fund details?", type: "select", options: ["Yes", "No"] },
					{ name: "provides_senior_secondary_education", label: "Do they provide senior secondary education?", type: "select", options: ["Yes", "No"] },
					{ name: "bank_name_for_grants", label: "Name of the bank in which the organization holds an account for receiving grants from this Ministry", type: "text" },
					{ name: "amount_received_from_ministry", label: "Amount received from the Ministry (FY Wise)", type: "number" },
					{ name: "amount_received_fy", label: "Amount received from the Ministry — FY Wise", type: "financial_year" },
					{ name: "non_recurring_grant_fy", label: "Non-recurring grant received by the organization (FY Wise)", type: "number" },
					{ name: "delay_in_receiving_grant", label: "Is there any delay in receiving grant?", type: "select", options: ["Yes", "No"] },
					{ name: "separate_bank_account_maintained", label: "Is separate bank account maintained for this scheme?", type: "select", options: ["Yes", "No"] },
					{ name: "school_establishment_date", label: "Date of establishment of the school", type: "date" },
				],
			},
			{
				title: "Number of students who got admission under this scheme",
				fields: [],
				classTable: {
					rowLabelHeader: "Class",
					valueHeader: "No. of Students",
					rows: [
						{ label: "9th", fields: [{ name: "admissions_9th", label: "No. of Students", type: "number" }] },
						{ label: "10th", fields: [{ name: "admissions_10th", label: "No. of Students", type: "number" }] },
						{ label: "12th", fields: [{ name: "admissions_12th", label: "No. of Students", type: "number" }] },
					],
				},
			},
			{
				title: "School Infrastructure & Students Details",
				columns: 4,
				fields: [
					{ name: "number_of_class_rooms", label: "Number of class Rooms", type: "number" },
					{ name: "number_of_teachers", label: "Number of teachers", type: "number" },
					{ name: "total_students_in_school", label: "Total number of students in the school", type: "number" },
					{ name: "teachers_min_qualification", label: "Does all the teacher have minimum qualification as per the guidelines?", type: "select", options: ["Yes", "No"] },
					{ name: "physical_education_teacher_appointed", label: "Is physical education teacher appointed in school?", type: "select", options: ["Yes", "No"] },
					{ name: "yoga_teacher_appointed", label: "Is there any Yoga teacher appointed in school?", type: "select", options: ["Yes", "No"] },
					{ name: "biometric_attendance_system", label: "Does the school have a biometric system in place for attendance tracking", type: "select", options: ["Yes", "No"] },
					{ name: "number_of_laboratories", label: "Number of laboratories in school", type: "number" },
					{ name: "rooms_have_electricity", label: "Whether all rooms have electricity and electrical equipment?", type: "select", options: ["Yes", "No"] },
					{ name: "number_of_staff_rooms", label: "Number of staff rooms", type: "number" },
					{
						name: "max_students_per_classroom_group",
						label: "Maximum number of students accommodated in one classroom",
						type: "group",
						groupFields: [
							{ name: "max_students_per_classroom", label: "Number of Students", type: "number" },
							{ name: "max_students_per_classroom_room_no", label: "Class Room", type: "text" },
						],
					},
					{
						name: "toilets_group",
						label: "Number of toilets (Separately for boys and Girls)",
						type: "group",
						groupFields: [
							{ name: "toilets_boys", label: "For Boys", type: "number" },
							{ name: "toilets_girls", label: "For Girls", type: "number" },
							{ name: "toilets_total", label: "Total", type: "number", computeFrom: ["toilets_boys", "toilets_girls"] },
						],
					},
					{ name: "drinking_water_available", label: "Is drinking water facility available for the students?", type: "select", options: ["Yes", "No"] },
					{ name: "hoarding_displayed", label: "Whether the institution has displayed hoarding indicating the name of the project", type: "select", options: ["Yes", "No"] },
					{ name: "passing_pct_10th_last_3yrs", label: "% of passing students in 10th Class in the last 3 years", type: "text" },
					{ name: "passing_pct_12th_last_3yrs", label: "% of passing students in 12th Class in the last 3 years", type: "text" },
					{ name: "counselor_available", label: "Is counselor available for students in school?", type: "select", options: ["Yes", "No"] },
					{ name: "uniform_4_sets_per_year", label: "Are the students given 4 sets of uniform (including shoes) in a year?", type: "select", options: ["Yes", "No"] },
					{ name: "desks_benches_count", label: "Number of desk and benches in all the classrooms", type: "number" },
					{ name: "first_aid_maintained", label: "Whether first aid box or emergency medicines are maintained in the school and hostel?", type: "select", options: ["Yes", "No"] },
					{ name: "doctor_visits_last_year", label: "No. of visits made by the doctor in last one year?", type: "number" },
					{ name: "games_sports_facilities_available", label: "Availability of games & sport facilities and play ground for the students", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Number of students admitted through scheme",
				fields: [],
				classTable: {
					rowLabelHeader: "Class",
					valueHeader: "No. of Students",
					rows: [
						{
							label: "9th",
							fields: [
								{ name: "admitted_scheme_9th_girls", label: "Girls", type: "number" },
								{ name: "admitted_scheme_9th_boys", label: "Boys", type: "number" },
								{ name: "admitted_scheme_9th_total", label: "Total", type: "number", computeFrom: ["admitted_scheme_9th_girls", "admitted_scheme_9th_boys"] },
							],
						},
						{
							label: "10th",
							fields: [
								{ name: "admitted_scheme_10th_girls", label: "Girls", type: "number" },
								{ name: "admitted_scheme_10th_boys", label: "Boys", type: "number" },
								{ name: "admitted_scheme_10th_total", label: "Total", type: "number", computeFrom: ["admitted_scheme_10th_girls", "admitted_scheme_10th_boys"] },
							],
						},
						{
							label: "12th",
							fields: [
								{ name: "admitted_scheme_12th_girls", label: "Girls", type: "number" },
								{ name: "admitted_scheme_12th_boys", label: "Boys", type: "number" },
								{ name: "admitted_scheme_12th_total", label: "Total", type: "number", computeFrom: ["admitted_scheme_12th_girls", "admitted_scheme_12th_boys"] },
							],
						},
					],
				},
			},
			{
				title: "Facilities in Hostel",
				columns: 3,
				fields: [
					{ name: "separate_hostel_boys_girls", label: "Is there a separate hostel for boys & girls?", type: "select", options: ["Yes", "No"] },
					{ name: "students_per_room_dormitory", label: "Number of students per room/dormitory in hostel being accommodated?", type: "number" },
					{ name: "separate_beds_bedding_material", label: "Whether all students have separate beds with bedding material", type: "select", options: ["Yes", "No"] },
					{ name: "clean_safe_drinking_water", label: "Provision for clean and safe drinking water", type: "select", options: ["Yes", "No"] },
					{ name: "toilets_bathrooms_clean_hygienic", label: "Whether the toilets and bathrooms were clean and hygienic?", type: "select", options: ["Yes", "No"] },
					{ name: "watchman_night_available", label: "Is watchman available during night in residential school?", type: "select", options: ["Yes", "No"] },
					{ name: "lady_watchman_girls_hostel", label: "Do they have lady watchman for girl's hostel?", type: "select", options: ["Yes", "No"] },
					{ name: "cctv_installed_working", label: "Do they have CCTV Cameras installed and working?", type: "select", options: ["Yes", "No"] },
					{ name: "kitchen_dining_room_available", label: "Is kitchen and dining room available", type: "select", options: ["Yes", "No"] },
					{ name: "three_meals_a_day", label: "Is three meals a day provided to the students?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Documents Related",
				columns: 3,
				fields: [
					{ name: "updated_cashbook_available", label: "Do they have updated cash book?", type: "select", options: ["Yes", "No"] },
					{ name: "asset_register_available", label: "Do they have asset register?", type: "select", options: ["Yes", "No"] },
					{ name: "uniform_purchase_distribution_records", label: "Records of purchase and distribution of uniforms", type: "select", options: ["Yes", "No"] },
					{ name: "doctor_visit_records", label: "Records of doctor's visit", type: "select", options: ["Yes", "No"] },
					{ name: "recognition_certificate_state_govt", label: "Recognition certificate of the education department of the State Government", type: "select", options: ["Yes", "No"] },
					{ name: "affiliation_certificate_board", label: "Affiliation Certificate of State/Central Board of Education", type: "select", options: ["Yes", "No"] },
				],
			},
		],
	},
	{
		matchName: "Schools under SHRESHTA Mode 1",
		description: "Social Audit Format for Verification of Residential School (for Mode-I School)",
		colorHex: "#4d7c0f",
		sections: [
			{
				title: "General Information",
				columns: 4,
				fields: [
					{ name: "financial_year", label: "Financial year", type: "financial_year", required: true },
					{ name: "state", label: "State", type: "text", defaultValue: "Tamil Nadu", readOnly: true },
					{ name: "district_id", label: "District", type: "district", required: true },
					{ name: "institute_id", label: "Institute", type: "institute", required: true, dependsOn: "district_id" },
					{ name: "project_name", label: "Name of the Project", type: "text" },
					{ name: "institution_name", label: "Name of the Institution/School", type: "text", required: true },
					{ name: "project_location_address", label: "Location of Project with full address", type: "text" },
					{ name: "project_specific_id", label: "Project Specific ID", type: "text" },
					{ name: "org_type", label: "Type of Organization", type: "select", options: ["Government", "NGO", "Private"] },
					{ name: "amount_received_from_ministry", label: "Amount received from the Ministry", type: "number" },
					{ name: "bank_name_for_grants", label: "Name of the bank in which the organization holds an account for the purpose of receiving grants from the Ministry", type: "text" },
					{ name: "school_establishment_date", label: "Date of establishment of the school", type: "date" },
				],
			},
			{
				title: "School Infrastructure & Students Details",
				columns: 4,
				fields: [
					{ name: "number_of_classrooms", label: "Number of classrooms", type: "number" },
					{ name: "number_of_teachers", label: "Number of teachers", type: "number" },
					{ name: "number_of_laboratories", label: "Number of laboratories", type: "number" },
					{ name: "rooms_have_electricity", label: "Whether all rooms have electricity and electrical equipment?", type: "select", options: ["Yes", "No"] },
					{ name: "number_of_staff_rooms", label: "Number of staff rooms", type: "number" },
					{
						name: "max_students_per_classroom_group",
						label: "Maximum number of students accommodated in one classroom",
						type: "group",
						groupFields: [
							{ name: "max_students_per_classroom_room_no", label: "Class Room", type: "text" },
							{ name: "max_students_per_classroom", label: "Number of Students", type: "number" },
						],
					},
					{
						name: "toilets_group",
						label: "Number of toilets (Separately for boys and Girls)",
						type: "group",
						groupFields: [
							{ name: "toilets_boys", label: "For Boys", type: "number" },
							{ name: "toilets_girls", label: "For Girls", type: "number" },
							{ name: "toilets_total", label: "Total", type: "number", computeFrom: ["toilets_boys", "toilets_girls"] },
						],
					},
					{ name: "drinking_water_available", label: "Is drinking water facility available in the school?", type: "select", options: ["Yes", "No"] },
					{ name: "hoarding_displayed", label: "Whether the institution has displayed hording indicating the name of the project", type: "select", options: ["Yes", "No"] },
					{
						name: "passing_pct_10th_group",
						label: "% of passing students in 10th Class in last 3 years?",
						type: "group",
						groupFields: [
							{ name: "passing_pct_10th_year1", label: "1. Year (%)", type: "text" },
							{ name: "passing_pct_10th_year2", label: "2. Year (%)", type: "text" },
							{ name: "passing_pct_10th_year3", label: "3. Year (%)", type: "text" },
						],
					},
					{
						name: "passing_pct_12th_group",
						label: "% of passing students in 12th Class in last 3 years?",
						type: "group",
						groupFields: [
							{ name: "passing_pct_12th_year1", label: "1. Year (%)", type: "text" },
							{ name: "passing_pct_12th_year2", label: "2. Year (%)", type: "text" },
							{ name: "passing_pct_12th_year3", label: "3. Year (%)", type: "text" },
						],
					},
					{
						name: "total_students_group",
						label: "Total number of students in the school",
						type: "group",
						groupFields: [
							{ name: "total_students_boys", label: "For Boys", type: "number" },
							{ name: "total_students_girls", label: "For Girls", type: "number" },
							{ name: "total_students_total", label: "Total", type: "number", computeFrom: ["total_students_boys", "total_students_girls"] },
						],
					},
					{ name: "counselor_available", label: "Is a counselor available for the students in school?", type: "select", options: ["Yes", "No"] },
				],
			},
			{
				title: "Number of students admitted through scheme last year",
				fields: [],
				classTable: {
					rowLabelHeader: "Class",
					valueHeader: "No. of Students",
					rows: [
						{
							label: "9th",
							fields: [
								{ name: "admitted_scheme_9th_girls", label: "Girls", type: "number" },
								{ name: "admitted_scheme_9th_boys", label: "Boys", type: "number" },
								{ name: "admitted_scheme_9th_total", label: "Total", type: "number", computeFrom: ["admitted_scheme_9th_girls", "admitted_scheme_9th_boys"] },
							],
						},
						{
							label: "10th",
							fields: [
								{ name: "admitted_scheme_10th_girls", label: "Girls", type: "number" },
								{ name: "admitted_scheme_10th_boys", label: "Boys", type: "number" },
								{ name: "admitted_scheme_10th_total", label: "Total", type: "number", computeFrom: ["admitted_scheme_10th_girls", "admitted_scheme_10th_boys"] },
							],
						},
						{
							label: "12th",
							fields: [
								{ name: "admitted_scheme_12th_girls", label: "Girls", type: "number" },
								{ name: "admitted_scheme_12th_boys", label: "Boys", type: "number" },
								{ name: "admitted_scheme_12th_total", label: "Total", type: "number", computeFrom: ["admitted_scheme_12th_girls", "admitted_scheme_12th_boys"] },
							],
						},
					],
				},
			},
			{
				title: "Bridge Course Related",
				columns: 3,
				fields: [
					{ name: "bridge_course_students_count", label: "Number of students taking bridge course", type: "number" },
					{ name: "bridge_course_timing", label: "Timing of the bridge course", type: "time" },
					{ name: "bridge_course_10pct_fee_received", label: "Has the institute received the 10% annual fee separately for providing bridge course?", type: "select", options: ["Yes", "No"] },
					{ name: "students_left_after_admission", label: "Did any student leave after taking admission? (student benefited under the scheme) if yes, mention the number", type: "number" },
				],
			},
			{
				title: "Facilities in Hostels",
				columns: 3,
				fields: [
					{ name: "separate_hostel_boys_girls", label: "Is there a separate hostel for boys & girls?", type: "select", options: ["Yes", "No"] },
					{ name: "students_per_room_dormitory", label: "Mention the number of students accommodated in a room/dormitory", type: "number" },
					{ name: "separate_beds_bedding_material", label: "Whether all students have separate beds with bedding material", type: "select", options: ["Yes", "No"] },
					{ name: "clean_safe_drinking_water", label: "Provision for clean and safe drinking water", type: "select", options: ["Yes", "No"] },
					{ name: "toilets_bathrooms_clean_hygienic", label: "Whether the toilets and bathrooms are clean and hygienic", type: "select", options: ["Yes", "No"] },
					{ name: "doctor_visit_frequency_per_month", label: "Frequency of doctor's visit in a month", type: "text" },
				],
			},
		],
	},
	{
		matchName: "Pre-Matric scholarship",
		description: "Students, gender, scholarship status, dropouts, grievances",
		colorHex: "#059669",
		sections: [
			{
				title: "Institution",
				columns: 3,
				fields: [
					{ name: "institution_name", label: "Name of school / institution", type: "text", required: true, placeholder: "Institution name" },
					{ name: "total_students", label: "Total no. of students", type: "number" },
				],
			},
			{
				title: "Student gender breakdown",
				columns: 3,
				fields: [
					{ name: "male_students", label: "Male students", type: "number" },
					{ name: "female_students", label: "Female students", type: "number" },
					{ name: "day_scholars", label: "Day scholars", type: "number" },
					{ name: "day_scholar_male", label: "Day scholar — male", type: "number" },
					{ name: "day_scholar_female", label: "Day scholar — female", type: "number" },
					{ name: "hostellers", label: "Hostellers", type: "number" },
					{ name: "hosteller_male", label: "Hosteller — male", type: "number" },
					{ name: "hosteller_female", label: "Hosteller — female", type: "number" },
				],
			},
			{
				title: "Scholarship awareness & application",
				columns: 3,
				fields: [
					{ name: "students_aware", label: "Students aware of scheme", type: "number" },
					{ name: "students_applied", label: "Students applied", type: "number" },
					{ name: "component1_students", label: "Component-1 students", type: "number" },
					{ name: "component2_students", label: "Component-2 students", type: "number" },
					{ name: "students_received", label: "Students received scholarship", type: "number" },
					{ name: "students_not_received", label: "Students NOT received", type: "number" },
					{ name: "awareness_source", label: "Major source of awareness", type: "select", options: ["Dept. of Social Welfare", "Through NGOs", "School teacher/administration", "Different media sources", "Any other"] },
					{ name: "payment_mode", label: "Mode of payment", type: "text", placeholder: "DBT / cheque / cash…" },
				],
			},
			{
				title: "Dropouts & left-outs",
				columns: 3,
				fields: [
					{ name: "left_out_prev_year", label: "Students left out (prev year)", type: "number" },
					{ name: "left_out_male", label: "Male left out", type: "number" },
					{ name: "left_out_female", label: "Female left out", type: "number" },
					{ name: "dropouts_total", label: "Total dropouts", type: "number" },
					{ name: "dropouts_male", label: "Male dropouts", type: "number" },
					{ name: "dropouts_female", label: "Female dropouts", type: "number" },
				],
			},
			{
				title: "Grievances & difficulties",
				columns: 3,
				fields: [
					{ name: "grievances_lodged", label: "Students lodged grievances (portal)", type: "number" },
					{ name: "gro_response_received", label: "Received response from GRO", type: "number" },
					{ name: "difficulty_caste_income_cert", label: "Difficulty in caste/income cert.", type: "number" },
					{ name: "difficulty_school_due_scheme", label: "Difficulties in school due to scheme", type: "number" },
				],
			},
			{
				title: "Remarks",
				columns: 1,
				fields: [
					{ name: "reasons_not_received", label: "Reasons for not getting scholarship", type: "textarea", placeholder: "Describe reasons…" },
					{ name: "remarks", label: "Remarks", type: "textarea", placeholder: "Remarks…" },
				],
			},
		],
	},
	{
		matchName: "Post-Matric scholarship",
		description: "Same structure as Pre-Matric, post-secondary level",
		colorHex: "#0284c7",
		sections: [
			{
				title: "Institution",
				columns: 3,
				fields: [
					{ name: "institution_name", label: "Name of institution", type: "text", required: true, placeholder: "Institution name" },
					{ name: "total_students", label: "Total students", type: "number" },
				],
			},
			{
				title: "Gender breakdown",
				columns: 3,
				fields: [
					{ name: "male_students", label: "Male students", type: "number" },
					{ name: "female_students", label: "Female students", type: "number" },
					{ name: "hostellers", label: "Hostellers", type: "number" },
					{ name: "hosteller_male", label: "Hosteller male", type: "number" },
					{ name: "hosteller_female", label: "Hosteller female", type: "number" },
				],
			},
			{
				title: "Scholarship status",
				columns: 3,
				fields: [
					{ name: "students_aware", label: "Students aware", type: "number" },
					{ name: "students_applied", label: "Students applied", type: "number" },
					{ name: "students_received", label: "Received scholarship", type: "number" },
					{ name: "students_not_received", label: "NOT received", type: "number" },
					{ name: "dropouts_total", label: "Dropouts", type: "number" },
					{ name: "grievances_lodged", label: "Grievances lodged", type: "number" },
				],
			},
			{
				title: "Remarks",
				columns: 1,
				fields: [{ name: "remarks", label: "Remarks", type: "textarea", placeholder: "Remarks…" }],
			},
		],
	},
	{
		matchName: "SC/ST Atrocities",
		description: "Case, nature, registration, relief amount, timeline",
		colorHex: "#dc2626",
		sections: [
			{
				title: "Case identification",
				columns: 3,
				fields: [
					{ name: "case_no_victim_name", label: "Case no. / Victim name", type: "text", required: true, placeholder: "Case no or victim name" },
					{ name: "victim_age", label: "Age of victim", type: "number", placeholder: "Age" },
					{ name: "atrocity_nature", label: "Nature of atrocity case", type: "text", placeholder: "Describe nature" },
					{ name: "crime_by_whom", label: "Crime committed by whom?", type: "text", placeholder: "Individual / group" },
				],
			},
			{
				title: "Legal status",
				columns: 3,
				fields: [
					{ name: "case_registered_scst_act", label: "Case registered under SC/ST Act 1989?", type: "yesno" },
					{ name: "charge_sheet_sent", label: "Charge sheet sent to court?", type: "yesno" },
					{ name: "accused_convicted_lower_court", label: "Accused convicted by lower court?", type: "yesno" },
				],
			},
			{
				title: "Relief amount",
				columns: 3,
				fields: [
					{ name: "relief_status", label: "Relief amount status", type: "select", options: ["Pending", "Sanctioned", "Allotted"] },
					{ name: "relief_to_whom", label: "Relief to whom?", type: "select", options: ["Victim", "Family member", "Dependent of victim"] },
					{ name: "relief_amount_total", label: "Total relief amount (₹)", type: "number" },
					{ name: "relief_within_7_days", label: "Relief provided within 7 days?", type: "yesno" },
					{ name: "actual_time_receiving_amount", label: "Actual time of receiving amount", type: "text", placeholder: "e.g. 15 days" },
				],
			},
			{
				title: "Remarks",
				columns: 1,
				fields: [{ name: "remarks", label: "Remarks", type: "textarea", placeholder: "Remarks…" }],
			},
		],
	},
	{
		matchName: "Inter-Caste Marriage",
		description: "Beneficiary, eligibility, incentive amount status",
		colorHex: "#ea580c",
		sections: [
			{
				title: "Beneficiary",
				columns: 2,
				fields: [
					{ name: "beneficiary_name", label: "Name of beneficiary", type: "text", required: true, placeholder: "Beneficiary name" },
					{ name: "spouse_sc_nonsc", label: "One spouse SC + other Non-SC?", type: "yesno" },
					{ name: "beneficiary_aware", label: "Beneficiary aware of provision?", type: "yesno" },
					{ name: "recommended_by", label: "Who recommended the application?", type: "text", placeholder: "Name / designation" },
				],
			},
			{
				title: "Incentive amount",
				columns: 3,
				fields: [
					{ name: "incentive_status", label: "Incentive status from ministry", type: "select", options: ["Pending", "Released 1st instalment", "Released 2nd instalment"] },
					{ name: "aware_fd_couple_name", label: "Aware of FD in couple's name?", type: "yesno" },
					{ name: "state_incentive_received", label: "State govt. incentive received?", type: "yesno" },
					{ name: "difficulty_receiving_amount", label: "Difficulty receiving amount?", type: "yesno" },
					{ name: "actual_time_receiving_amount", label: "Actual time of receiving amount", type: "text", placeholder: "e.g. 30 days" },
				],
			},
			{
				title: "Remarks",
				columns: 1,
				fields: [{ name: "remarks", label: "Remarks", type: "textarea", placeholder: "Remarks…" }],
			},
		],
	},
	{
		matchName: "NSFDC",
		description: "SC beneficiary loan — purpose, amount, repayment, income",
		colorHex: "#9333ea",
		sections: [
			{
				title: "Beneficiary profile",
				columns: 3,
				fields: [
					{ name: "beneficiary_name", label: "Name of beneficiary", type: "text", required: true, placeholder: "Full name" },
					{ name: "gender", label: "Gender", type: "select", options: ["Male", "Female", "Transgender"] },
					{ name: "category", label: "Category", type: "text", placeholder: "SC / Other" },
					{ name: "annual_family_income", label: "Annual family income (₹)", type: "number" },
					{ name: "loan_purpose", label: "Purpose of loan", type: "text", placeholder: "Business / agriculture…" },
					{ name: "implementing_agency", label: "Implementing agency", type: "select", options: ["State Channelizing Agency", "Public Sector Bank", "Regional Rural Bank", "Non-Banking Financial Body", "Micro Finance Institutions"] },
					{ name: "loan_year", label: "Year of loan availed", type: "number", placeholder: "Year" },
				],
			},
			{
				title: "Loan details",
				columns: 3,
				fields: [
					{ name: "loan_fully_received", label: "Loan fully received?", type: "yesno" },
					{ name: "amount_received", label: "Amount received (₹)", type: "number" },
					{ name: "amount_repaid", label: "Amount repaid (₹)", type: "number" },
					{ name: "extra_payment_required", label: "Beneficiary had to pay extra?", type: "yesno" },
					{ name: "income_generated", label: "Income generation from activity?", type: "yesno" },
					{ name: "beneficiary_eligible", label: "Beneficiary was eligible?", type: "yesno" },
				],
			},
			{
				title: "Satisfaction",
				columns: 2,
				fields: [
					{ name: "satisfaction_level", label: "Satisfaction level of beneficiary", type: "select", options: ["Very Good", "Good", "Average", "Poor", "Very Poor"] },
				],
			},
			{
				title: "Remarks",
				columns: 1,
				fields: [{ name: "remarks", label: "Remarks", type: "textarea", placeholder: "Remarks…" }],
			},
		],
	},
	{
		matchName: "Garima Greh",
		description: "Shelter home for transgender persons — registers, infra, staff",
		colorHex: "#be185d",
		sections: [
			{
				title: "Basic information",
				columns: 3,
				fields: [
					{ name: "shelter_name", label: "Name of Garima Greh", type: "text", required: true, placeholder: "Shelter name" },
					{ name: "address", label: "Address", type: "text", placeholder: "Full address" },
					{ name: "commencement_date", label: "Date of commencement", type: "date" },
					{ name: "grant_commencement_year", label: "Year of grant-in-aid commencement", type: "number", placeholder: "Year" },
					{ name: "total_staff", label: "Total no. of staffs", type: "number" },
					{ name: "total_residents", label: "Total no. of residents", type: "number" },
					{ name: "pmc_composition", label: "PMC composition", type: "text", placeholder: "Project Management Committee" },
				],
			},
			{
				title: "Registers & records",
				columns: 3,
				fields: [
					{ name: "admission_register", label: "Admission register", type: "yesno" },
					{ name: "residents_history_register", label: "Residents history register", type: "yesno" },
					{ name: "visit_inspection_register", label: "Visit/inspection register", type: "yesno" },
					{ name: "doctor_visit_register", label: "Doctor visit register", type: "yesno" },
					{ name: "asset_register", label: "Asset register", type: "yesno" },
					{ name: "medicine_stock_register", label: "Medicine stock register", type: "yesno" },
					{ name: "ration_stock_register", label: "Ration/vegetable stock register", type: "yesno" },
					{ name: "residents_movement_register", label: "Residents movement register", type: "yesno" },
					{ name: "staff_attendance_register", label: "Staff attendance register", type: "yesno" },
					{ name: "staff_honorarium_register", label: "Staff honorarium register", type: "yesno" },
					{ name: "bank_passbook", label: "Bank passbook", type: "yesno" },
					{ name: "passbook_updated", label: "Passbook updated?", type: "yesno" },
					{ name: "cash_ledger_book", label: "Cash/ledger book", type: "yesno" },
					{ name: "network_directory", label: "Network directory", type: "yesno" },
					{ name: "visitors_register", label: "Visitors register", type: "yesno" },
					{ name: "disaster_hazard_plan", label: "Disaster/hazard management plan", type: "yesno" },
				],
			},
			{
				title: "Infrastructure",
				columns: 3,
				fields: [
					{ name: "peaceful_locality", label: "Peaceful/residential locality?", type: "yesno" },
					{ name: "built_up_space_sqft", label: "Total built-up space (sqft)", type: "number" },
					{ name: "accessibility_cleanliness_safety", label: "Accessibility/cleanliness/safety", type: "select", options: ["Good", "Average", "Poor"] },
					{ name: "transparency_board", label: "Transparency board available?", type: "yesno" },
					{ name: "attendance_type", label: "Attendance type", type: "select", options: ["Manual", "Biometric"] },
					{ name: "kitchen_items_available", label: "Kitchen items available?", type: "yesno" },
					{ name: "cctv_available", label: "CCTV available?", type: "yesno" },
					{ name: "adequate_rooms", label: "Adequate rooms/dormitories?", type: "yesno" },
					{ name: "medical_counseling_room", label: "Medical/counseling room?", type: "yesno" },
					{ name: "recreational_facilities", label: "Recreational facilities?", type: "yesno" },
					{ name: "total_bathrooms_toilets", label: "Total bathrooms & toilets", type: "number" },
					{ name: "separate_staff_room", label: "Separate staff room?", type: "yesno" },
					{ name: "skill_development_room", label: "Skill development room?", type: "yesno" },
					{ name: "audio_visual_equipment", label: "Audio-visual equipment?", type: "yesno" },
					{ name: "computer_laptop_office", label: "Computer/laptop for office?", type: "yesno" },
					{ name: "specially_abled_facilities", label: "Special facilities for specially-abled?", type: "yesno" },
				],
			},
			{
				title: "Staff responsibilities",
				columns: 3,
				fields: [
					{ name: "suggestion_box", label: "Suggestion box maintained?", type: "yesno" },
					{ name: "daily_menu_displayed", label: "Daily menu displayed?", type: "yesno" },
					{ name: "medical_facilities_available", label: "Medical facilities available?", type: "yesno" },
					{ name: "pd_meetings_pmc_approval", label: "PD meetings with PMC approval?", type: "yesno" },
					{ name: "regular_feedback_meetings", label: "Regular feedback meetings?", type: "yesno" },
					{ name: "counseling_sessions", label: "Counseling sessions by counselor?", type: "yesno" },
					{ name: "awareness_programmes", label: "Awareness programmes conducted?", type: "yesno" },
					{ name: "quarterly_report_submitted", label: "Quarterly report submitted?", type: "yesno" },
					{ name: "stock_inventory_examined", label: "Stock & inventory examined?", type: "yesno" },
					{ name: "annual_inspection_local_authority", label: "Annual inspection by local authority?", type: "yesno" },
				],
			},
			{
				title: "Remarks",
				columns: 1,
				fields: [{ name: "remarks", label: "Remarks", type: "textarea", placeholder: "Remarks…" }],
			},
		],
	},
];

export const getDsjeSubSchemeFormConfig = (
	subSchemeName: string | undefined,
): DsjeSubSchemeFormConfig | undefined =>
	dsjeSubSchemeFormConfigs.find((c) => c.matchName === subSchemeName);
